/**
 * stepRun — one fixed-timestep tick, in the normative order of
 * docs/run-engine-spec.md section 5.
 *
 * This is the ONLY mutator. Live play and replay both drive it, which is
 * what makes them the same code path rather than two paths that drift.
 */
import { Phase, Outcome, Stream, Ev, RUN_MAX_TICKS, TICK_RATE,
  ChargeTier, CHARGE_PARTIAL, CHARGE_FULL, Titan, DeathCause } from './constants.js';
import { emit, unitDir } from './events.js';
import { nextFloat, nextInt, nextRange } from './rng.js';
import { poolAlloc, poolFree, type RunState } from './state.js';
import { recomputeStats } from './stats.js';
import type { TickInput } from './input.js';
import { DIR_STEPS, MAG_MAX } from './input.js';
import type { GameConfig } from './config.js';

const GATE_OP_CODE = { add: 0, subtract: 1, multiply: 2, armory: 3, barrier: 4, armscrate: 5, vehicle: 6, ally: 7, armour: 8 } as const;
export const GATE_ADD = 0, GATE_SUB = 1, GATE_MUL = 2, GATE_ARMORY = 3, GATE_BARRIER = 4;
/**
 * ARMS CRATE: a barrier with a gun locked inside it.
 *
 * The reference ads hang the reward visibly above a destructible block --
 * a weapon, a vehicle, a hero -- so upgrading is something you SHOOT YOUR
 * WAY INTO rather than something you walk past. A plain armoury gate hands
 * the gun over for steering into a lane, which is no challenge at all and
 * is why the ten-gun library never felt earned.
 *
 * Break it before it reaches you and the gun is yours. Ram it intact and
 * it costs bodies and gives nothing.
 */
export const GATE_CRATE = 5;
/** A crate whose prize is a VEHICLE rather than a gun. */
export const GATE_VEHICLE = 6;
/** A station whose prize is a MONSTER that fights beside you. */
export const GATE_ALLY = 7;
/** A station whose prize is ARMOUR: damage the squad does not pay for. */
export const GATE_ARMOUR = 8;

/**
 * Monolith footprint, in world units. It fills most of its lane and is
 * deep enough that fire aimed up the lane genuinely lands on it -- a thin
 * slab would let a moving squad thread rounds past and the rock would
 * chip only by accident.
 */
const MONOLITH_HALF_W = 1.35;
const MONOLITH_HALF_D = 1.9;

/**
 * Raise a monolith at the edge of the corridor.
 *
 * hp is authored huge on purpose: the reference sets a five-figure counter
 * and lets it fall for most of a minute. A number that empties in three
 * seconds is a crate, and a crate cannot pose a standing question.
 */
const BLOCK_TITAN = 0, BLOCK_BODIES = 1, BLOCK_CRATE = 2, BLOCK_ARMOUR = 3, BLOCK_VEHICLE = 4;
/**
 * ISLE-12 REBUILD (founder finding, 2026-08-25): chapter 12's two-lane
 * add/armour gate pair is replaced entirely by ONE road-width destructible
 * object per group -- "one destructible object standing in the road with a
 * number on it, shot down to claim what's inside." A distinct payload from
 * BLOCK_BODIES, not a reuse: BLOCK_BODIES pays its authored `value` flat
 * every time, and chapters 1-11's own monoliths already depend on that
 * exactly (byte-identical is binding). BLOCK_RACE pays a TIME-weighted
 * fraction of `value` (see breakBlock) and costs the player on a loss
 * (see the monolith drift loop) -- both genuinely new mechanics.
 */
const BLOCK_RACE = 5;
const BLOCK_OPS: Record<string, number> = {
  titan: BLOCK_TITAN, bodies: BLOCK_BODIES, crate: BLOCK_CRATE,
  armour: BLOCK_ARMOUR, vehicle: BLOCK_VEHICLE, race: BLOCK_RACE,
};
function spawnMonolith(
  s: RunState, lane: number, hp: number, titan: number, lifeSec: number,
  payloadOp = BLOCK_TITAN, payloadValue = 0,
): void {
  const idx = poolAlloc(s.mp);
  if (idx < 0) return;
  const r = s.config.rules;
  // IN THE LANE, NOT BESIDE THE ROAD.
  //
  // FOUNDER RETRACTION 2026-08-14: this used to sit on the shoulder, past
  // the kerb, unreachable by construction -- "completely useless in its
  // form and never reachable." Retracted for the forge isle first (the
  // rock stands IN the reward lane, the titan unveiling happens where the
  // player's guns already point) and extended to every chapter here
  // (BEAT_GRAMMAR.md diagnostic #2, 2026-08-20): the shoulder branch was
  // never removed for non-forge isles, so every monolith outside chapter 1
  // rendered 0.74 units past the kerb, off the road surface. `lane` is
  // authored per monolith row (monoliths.csv) whether or not the isle has
  // a fixed reward lane, so it's the right value to place from either way.
  // Physics law holds exactly as it did for the forge isle: the rock
  // expires while still ahead of the squad (see the drift block below),
  // so it can never overlap a body.
  const lanes = Math.max(1, r.laneCount);
  // ISLE-12 REBUILD, CORRECTED (founder finding, 2026-08-25): a race
  // monolith stands in ONE lane, leaving the other open -- centring it on
  // arenaWidth/2 (the prior version of this line) made it occupy the
  // whole road, which is what a two-lane pair with no open lane looks
  // like from the other direction. `lane` (monoliths.csv, alternated
  // group to group) is now read the same way every other payload reads
  // it.
  s.mX[idx] = laneCenter(r.arenaWidth, lanes, lane);
  // VISIBLE-KILL AUDIT PART 2 (founder finding, 2026-08-27): the 0.55
  // fraction is now `r.monolithSpawnFrac` (0.55 everywhere unoverridden).
  // Measured on isle 12 at the shared 0.55 (spawnAhead 60 -> spawn at 33
  // units out): distance from the player never left [31.6, 33] for the
  // monolith's whole life, so the reveal always happened far away and
  // small. See ChapterDef.monolithSpawnFrac.
  s.mY[idx] = s.playerY + r.spawnAhead * r.monolithSpawnFrac;
  s.mHp[idx] = hp; s.mHpMax[idx] = hp;
  s.mLane[idx] = lane;
  s.mTitan[idx] = titan;
  s.mLife[idx] = Math.round(lifeSec * TICK_RATE);
  s.mOp[idx] = payloadOp; s.mValue[idx] = payloadValue;
  emit(s, Ev.Monolith, idx, titan);
}
/**
 * Lane commitment. The genre does NOT use analog steering: the squad
 * occupies a lane and SNAPS between them. Free horizontal movement makes
 * the squad float, blurs which lane you are actually in, and turns a
 * binary gate decision into a smear. Every driver — human input and bot
 * policy alike — goes through this so they cannot diverge.
 */
export function laneCenter(arenaWidth: number, laneCount: number, lane: number): number {
  const l = lane < 0 ? 0 : lane >= laneCount ? laneCount - 1 : lane;
  return ((l + 0.5) / laneCount) * arenaWidth;
}

/** Which lane a world-x sits in. */
export function laneOf(arenaWidth: number, laneCount: number, x: number): number {
  const l = Math.floor((x / arenaWidth) * laneCount);
  return l < 0 ? 0 : l >= laneCount ? laneCount - 1 : l;
}

/**
 * Input that drives the squad to a lane centre and then HOLDS it. Full
 * magnitude while travelling, zero on arrival — no easing tail, which is
 * what produced the floating.
 */
/**
 * Convert "the finger wants the squad HERE" into the sim's quantised
 * (dir, mag) domain.
 *
 * Full speed until the last `softZone` units, then proportional. The two
 * obvious alternatives both fail: a constant magnitude overshoots and
 * oscillates around the target, and a magnitude proportional across the
 * whole corridor crawls at distance and reads as input lag. Neither is
 * 1:1 with a finger.
 */
export function followInput(
  softZone: number, playerX: number, targetX: number,
): { dir: number; mag: number } {
  const dx = targetX - playerX;
  const adx = dx < 0 ? -dx : dx;
  if (adx < 0.015) return { dir: 0, mag: 0 };
  let mag = adx >= softZone ? MAG_MAX : Math.round((adx / softZone) * MAG_MAX);
  if (mag < 1) mag = 1;
  if (mag > MAG_MAX) mag = MAG_MAX;
  return { dir: dx > 0 ? 0 : 128, mag };
}

/**
 * Half-width of the curtain of fire, in world units either side of the
 * squad centre.
 *
 * Capped against the LANE, not the arena. Against the arena
 * (0.34 * 7.2 = 2.45) a large squad sprayed 2.45 units either side while
 * lane centres are only 3.6 apart, so past roughly 90 bodies the formation
 * was shooting into the NEIGHBOURING lane -- measured killing a boss it had
 * never committed to, for the same damage as fighting it head on. That
 * defeats lane commitment, which is the one decision the corridor is built
 * around.
 */
export function fireHalfWidth(
  arenaWidth: number, laneCount: number, squadSize: number,
): number {
  // The curtain spans the LANE, always. It used to be 0.26 * sqrt(squad),
  // so a squad of 3 covered 0.9 units of a 3.6-wide lane -- and once the
  // horde was packed shoulder-to-shoulder across the full width, roughly
  // three quarters of it walked straight past the fire untouched. Measured:
  // enemies standing 0.3 units from the squad with the kill count frozen.
  //
  // Squad size widens the curtain too, with diminishing returns -- a flat
  // 0.46*laneW regardless of squad size read as "more soldiers only makes
  // it thicker", never wider, and a reward that grows the squad produced no
  // felt change on screen. n^2/(n^2+K^2) (a Hill curve, K=12) sits within a
  // few percent of the historical constant for squad 1-4 -- the early game
  // is already tuned and must not retune itself here -- then rises visibly
  // through squad 10-20, and flattens hard past ~30. The cap (2/3 of the
  // lane) is bounded by TWO separate concerns, both covered by
  // boss-engagement.test.ts: the curtain itself must stay inside the lane
  // with margin, and a horde-spawnable enemy (spawn_weight > 0, fattest is
  // rift_brute at 0.78u -- the boss and beast are lane-authored and never
  // stand at an arbitrary boundary, so they don't bound this) must never
  // be reachable from the lane next door. 2.4u clears both: 1.2u of margin
  // under the 3.6u lane, and 0.42u of margin once rift_brute's own radius
  // is added back.
  const laneW = arenaWidth / Math.max(1, laneCount);
  const base = laneW * 0.46;
  const cap = laneW * (2 / 3);
  const K = 12;
  const n = squadSize > 0 ? squadSize : 0;
  const t = (n * n) / (n * n + K * K);
  return base + (cap - base) * t;
}

export function laneInput(
  arenaWidth: number, laneCount: number, playerX: number, lane: number,
): { dir: number; mag: number } {
  const target = laneCenter(arenaWidth, laneCount, lane);
  const dx = target - playerX;
  const adx = dx < 0 ? -dx : dx;
  if (adx < 0.05) return { dir: 0, mag: 0 };
  return { dir: dx > 0 ? 0 : 128, mag: 15 };
}

/**
 * Horde packing, in world units. Shoulder-to-shoulder across the lane and
 * half a body-depth between ranks: the difference between "a wall of
 * bodies coming at you" and "some enemies".
 */
/**
 * The shared walking pace of the ordinary horde, in units/sec. Taken from
 * the mid-weight body so the mass moves at a believable crowd speed.
 */
const HORDE_SPEED = 3.9;
const HORDE_FILE_GAP = 0.26;
const HORDE_RANK_GAP = 0.30;
/**
 * MIRROR -- source of truth is scene3d.ts's deck-material far-edge-fade
 * zone (documented there, and in tuning.ts's own PIER_WRAP_FLOOR_MIRROR/
 * DECK_FADE_END_MIRROR pair, as 228-240: the fade starts at 228 and
 * reaches full invisibility at 240). Duplicated here rather than imported
 * because packages/run-engine is a pure-TS sim boundary with zero DOM/
 * Pixi/Three imports (CLAUDE.md's hard sim/render boundary) -- the render
 * module cannot be a dependency of this one.
 *
 * DECK_UNDIMMED_TERMINUS_UNITS is the last depth the deck is still shown
 * at full strength -- the deepest a body can spawn and still read as
 * "standing in the corridor" rather than "materialising in the fade".
 * DECK_INVISIBLE_EDGE_UNITS is past full fade -- a body spawned here is
 * genuinely offscreen and can only be seen walking IN, never popping in.
 */
const DECK_UNDIMMED_TERMINUS_UNITS = 228;
const DECK_INVISIBLE_EDGE_UNITS = 240;
/**
 * D13 crossover range, world units ahead of the player. Derived from
 * FRAME_SPEC.md §10's measured reference contact band (screen01.y
 * 0.70-0.80) projected through the frozen camera (CAM_HEIGHT=7,
 * CAM_BACK=8.5, CAM_LOOKAT_FORWARD=7, CAM_FOV=50): screen01.y=0.70 ->
 * dy=1.34 world units.
 */
const CROSSOVER_RANGE_UNITS = 1.34;

/**
 * SQUAD FORMATION CONSTANTS (Contact and Impact, "squad bodies" part 1).
 * Mirror scene3d.ts's own module-level constants, byte-for-byte, so the
 * ported formula in the formation section below reproduces the renderer's
 * pre-port output exactly. MIRROR -- authoritative source is scene3d.ts;
 * update these if any of the cited lines change there.
 */
const FORM_FILE_GAP = 0.70, FORM_RANK_GAP = 0.78; // scene3d.ts:914
const FORM_SOLDIER_SCALE = 1.02; // scene3d.ts:504
const FORM_SQUAD_MULT = 0.864; // scene3d.ts:512
const FORM_SOLDIER_BULK = 1.12; // scene3d.ts:520
/** scene3d.ts:114/123 -- S=1, SX=S*1.875. Named for what they scale
 * (depth/lateral), not reused across files: these are two of a kind. */
const FORM_S = 1, FORM_SX = FORM_S * 1.875;
/**
 * scene3d.ts:2092-2095 -- SOLDIER_WORLD_H / the loaded character.glb's
 * own bind-pose bounding-box height, measured once at asset load
 * (rigH=1.7517244511285994 for the shipped rig, read live via a debug
 * probe, 2026-08-24). Asset-derived, not a design constant -- there is
 * no sim-side equivalent to compute it from, so it is mirrored as a
 * literal. Would need updating if the character rig is ever re-exported
 * at different proportions; scene3d.ts's own RIG_FIT stays authoritative.
 */
const FORM_RIG_FIT = 1.4785430427321586;
/**
 * Soldier collision radius, "Contact and Impact, squad bodies" part 2.
 * Derived from the SAME bind-pose Box3 the rigH/RIG_FIT measurement above
 * used (scene3d.ts:2092), read via the same live debug probe (2026-08-24):
 * minX -0.9346, maxX 0.9346, minZ -0.1788, maxZ 0.2068. X is a T-pose
 * arm-span (~1.87 native units, nearly the rig's own height) and is NOT a
 * usable body width -- the bind pose has arms held straight out, not at
 * the sides. Z (front-to-back chest/hip depth, 0.3856 native units) is the
 * one bind-pose axis a T-pose does not distort, so it is what this radius
 * is derived from, not X. No SX unit conversion is needed: Z carries no
 * scale difference between sim-space and world-space (FORM_S = 1), so a
 * world-space depth measurement IS a sim-space one.
 */
const FORM_RIG_DEPTH_NATIVE = 0.38563385313629106;
/** Separation solver sweeps per tick -- see its own doc comment in the
 * "15c. squad & enemy separation" section for the measurement behind it. */
const SEPARATION_ITERATIONS = 8;
/** A many-body chain's relaxation is asymptotic, not exact, at any finite
 * iteration count -- measured leaving the tightest pair a fraction of a
 * percent short of the true radius sum even at 20 sweeps. A 1% margin on
 * the SOLVER's target (never on the reported/measured radius sum itself)
 * converges the common case in well under SEPARATION_ITERATIONS sweeps
 * instead of chasing the tail, at a separation no eye can register. */
const SEPARATION_MARGIN = 1.01;
/**
 * PACKING RADIUS (founder, 2026-09-06, horde density fix). `def.radius`
 * is load-bearing well beyond separation -- projectile hit-testing
 * (step.ts's own projectile-vs-enemy pass), player/squad contact damage,
 * ally contact damage, AI stop-distance, the lane-bound clamp, AND the
 * enemy's own rendered visual size (scene3d.ts's `massBodyScale`) -- so
 * shrinking it globally to loosen packing would shrink hit windows and
 * the enemy's own body on screen, not just how close bodies rest.
 * `SEPARATION_MARGIN` is already barely above 1.0, the minimum that
 * guarantees zero interpenetration (Iron Rule 8) -- it has no headroom
 * to loosen without allowing actual overlap.
 *
 * This constant exists so the TWO SEPARATION BLOCKS ONLY (soldier-vs-
 * enemy below, enemy-vs-enemy right after) can rest bodies closer than
 * their real combat/visual radius would allow, without touching that
 * radius anywhere else. `corridor_mass`'s own packing formula
 * (`HORDE_FILE_GAP=0.26`, step.ts's spawn function) authors a rest pitch
 * of ~laneW/perRank; at the unscaled radius (0.34 for rift_skitterer)
 * the enforced floor was `2*0.34*1.01=0.6868` -- more than double the
 * authored pitch, so every spawn immediately fought the separation pass
 * back open. At 0.4, rift_skitterer's packing radius is 0.136, floor
 * `2*0.136*1.01=0.2747` -- just above the 0.26 pitch corridor_mass
 * already authors (matching that same code's own "shoulder-to-shoulder
 * is ~0.34, the skitterer radius" framing for what counts as touching,
 * not overlapping).
 */
const PACKING_RADIUS_FRACTION = 0.4;

/**
 * Deterministic per-enemy conversion, from the enemy's own pool slot --
 * NOT `s.rng`/`nextFloat`, so it never perturbs the shared RNG stream or
 * needs float trig to agree across engines. Knuth's multiplicative hash,
 * mod 2^32, normalised to [0,1).
 */
function crossoverHash(slot: number): number {
  return ((slot * 2654435761) >>> 0) / 4294967296;
}

/**
 * How many streams a volley fires: EVERY soldier fires the weapon, so a
 * lone rifleman lays exactly one line and thirty soldiers lay thirty --
 * the squad's growth is drawn on screen in fire, not implied by a number.
 * Multi-barrel weapons multiply per soldier (a scattergun is three pellets
 * per man). The old formula (weapon base + squad/step) meant one soldier
 * fired three lines and a tripled squad barely changed the picture.
 *
 * The ONE shared source for sim, HUD and renderer; a second copy of this
 * arithmetic is how the muzzle count and the bolt count drift apart.
 */
export function volleySize(
  squadSize: number, projectilesPerShot: number, cap: number,
): number {
  const n = squadSize * projectilesPerShot;
  return n < 1 ? 1 : n > cap ? cap : n;
}

export const PICKUP_XP = 0;
export const PICKUP_WEAPON = 1;
/** A body that joins the squad. Dropped by kills, so combat IS progression. */
export const PICKUP_RECRUIT = 2;

/** Gate application. Multiply-then-clamp — the order is balance-visible. */
export function applyGateOp(squad: number, opCode: number, value: number, squadMax: number): number {
  let next: number;
  if (opCode === GATE_OP_CODE.add) next = squad + value;
  else if (opCode === GATE_OP_CODE.subtract) next = squad - value;
  else next = squad * value;
  // Floor applies to EVERY op, not just multiply: a fractional gate value
  // on add/subtract would otherwise yield a fractional squad size, and a
  // squad is a count of bodies. Found by the [0, squadMax] property test.
  next = Math.floor(next);
  // A multiplicative penalty (value<1) on a live squad must not floor to 0
  // -- that's a silent instant wipe on a single wrong-lane hit, not a
  // survivable proportional cost. subtract/armour ops reaching 0 stay an
  // intended kill; only the multiply-below-1 case is clamped up to 1
  // (A-25 rung-penalty fix, founder, 2026-08-20).
  if (opCode === GATE_OP_CODE.multiply && value < 1 && squad > 0 && next < 1) next = 1;
  if (next < 0) next = 0;
  if (next > squadMax) next = squadMax;
  return next;
}

/**
 * LANE-BLEED FIX, RE-SOLVED (2026-08-26, founder finding): a boss/beast's
 * stop distance and contact radius are sized to its own drawn silhouette
 * -- legitimately several units, since stormhulk/gale_colossus both need
 * real clearance for their attack swing (constants.ts RADIUS_PER_SCALE).
 * Lanes are only ~3.6 units apart on a 2-lane isle, so a radius sized for
 * that swing reaches clean across the lane boundary if nothing else
 * gates it.
 *
 * FIRST FIX gated on discrete lane INDEX (`laneOf`, a hard floor-divide
 * boundary at the exact arena midpoint) and made the boss read as
 * "unshootable" in real play: nothing on screen marks that exact x, a
 * body-scale boss visually spans well past its own lane's width, and a
 * human a few centimetres over the invisible line got a full, silent
 * refusal. Gated on LATERAL DISTANCE from the body instead -- forgiving
 * near the boundary (still well short of reaching the OTHER lane's own
 * centre, `laneW` away), matching what the screen actually shows rather
 * than a division the player has no way to see.
 */
function lateralInRange(ex: number, x: number, r: { arenaWidth: number; laneCount: number }): boolean {
  const laneW = r.arenaWidth / Math.max(1, r.laneCount);
  return Math.abs(x - ex) <= laneW * 0.6;
}

/**
 * A boss/beast that has stopped in Y never adjusted X at all -- D13
 * crossover explicitly excludes them (`s.eIsBoss[i] !== 1` gate, the
 * main enemy loop above), so once stopped a boss was pinned to whatever
 * lane-centre X it approached on for the rest of its life. Contact
 * (section 12) and its own melee gate both require `lateralInRange`,
 * an actual X-distance check -- a player who ever steps sideways once
 * the boss has stopped (chasing a gate, holding the reward lane, simply
 * dodging) walks the boss's OWN target clean out of its reach, and nothing
 * ever closed that gap again. Measured live: a fight this should turn into
 * a wave of the boss's hand connecting on nothing. Chases at the boss's
 * own forward speed (`spd`, already computed for this tick), capped so a
 * single tick's nudge can never overshoot past the player's X.
 */
function trackPlayerX(s: RunState, i: number, spd: number): void {
  const dx = s.playerX - s.eX[i];
  const step = spd / TICK_RATE;
  s.eX[i] += Math.abs(dx) < step ? dx : Math.sign(dx) * step;
}

/** Per-soldier hp for THIS run: the base rule times the character's mult. */
export function unitHp(s: RunState): number {
  const ch = s.config.characters[s.characterIdx];
  return s.config.rules.squadUnitHp * (ch ? ch.squadHpMult : 1);
}

function setSquad(s: RunState, size: number): void {
  s.squadSize = size;
  s.squadHpPool = size * unitHp(s);
  if (size > s.squadPeak) s.squadPeak = size;
}

/**
 * Grow the squad, converting anything over the cap into XP.
 *
 * The cap was reached around 135s of a 200s chapter, and every body earned
 * after that simply evaporated -- gates, recruits and boss drops all became
 * no-ops for the last third of the run, which is exactly when the pressure
 * is highest. Overflow now feeds the level track instead, so a maxed squad
 * keeps converting the corridor into power rather than into nothing.
 */
function gainSquad(s: RunState, want: number): void {
  const cap = s.config.rules.squadMax;
  if (want <= cap) { setSquad(s, want); return; }
  const overflow = want - cap;
  setSquad(s, cap);
  s.xp += Math.round(overflow * s.config.rules.overflowXpPerBody);
}

/** Boss phase thresholds. Crossing one is an event the presentation needs. */
function updateBossPhase(s: RunState, e: number): void {
  if (e !== s.bossIdx) return;
  const def = s.config.enemies[s.eDefIdx[e]]!;
  const frac = s.eHp[e] / def.hp;
  const phase = frac <= 0.34 ? 2 : frac <= 0.67 ? 1 : 0;
  if (phase > s.bossPhase) {
    s.bossPhase = phase;
    emit(s, Ev.BossPhase, -1, phase, Math.round(frac * 100));
  }
}

/** Weighted draw without replacement from upgrades not yet maxed. */
function drawOffers(s: RunState): void {
  const cfg = s.config;
  const taken = new Set<number>();
  s.offerCount = 0;
  const want = Math.min(cfg.rules.levelUpOffers, s.offerOptions.length);

  for (let pick = 0; pick < want; pick++) {
    let total = 0;
    for (let i = 0; i < cfg.upgrades.length; i++) {
      if (taken.has(i)) continue;
      if (s.upgradeStacks[i] >= cfg.upgrades[i]!.maxStacks) continue;
      total += cfg.upgrades[i]!.rarityWeight;
    }
    if (total <= 0) break;
    let roll = nextFloat(s.rng, Stream.UpgradeOffers) * total;
    for (let i = 0; i < cfg.upgrades.length; i++) {
      if (taken.has(i)) continue;
      if (s.upgradeStacks[i] >= cfg.upgrades[i]!.maxStacks) continue;
      roll -= cfg.upgrades[i]!.rarityWeight;
      if (roll <= 0) {
        taken.add(i);
        s.offerOptions[s.offerCount++] = i;
        break;
      }
    }
  }
  s.offerKind = 0;
}

/**
 * Difficulty tier. Escalation must be a SYSTEM, not a hand-authored wave
 * list: with a fixed list the twentieth wave is exactly as hard as the
 * author guessed, and the curve flattens the moment the player outgrows
 * it. Every enemy is scaled by the tier live at its spawn, so the same
 * body type keeps getting meaner as the run goes on.
 */
export function difficultyTier(s: RunState): number {
  // Two terms. TIME drives the baseline escalation, and POWER keeps it
  // honest: firepower scales with squad size, so a 240-strong squad puts
  // out ~60 streams and trivially out-damages any fixed enemy stat block.
  // Without the second term the curve is only ever as hard as the moment
  // before the player's first big multiply gate.
  const byWave = s.waveCursor * s.config.rules.difficultyRamp;
  const byPower = s.squadPeak * s.config.rules.difficultyPerSquad;
  return 1 + byWave + byPower;
}

/**
 * The best gun the run is allowed to have handed out YET.
 *
 * Weapons are randomised so two runs do not walk the same ten rungs in
 * the same order, but the ladder still has to climb: weak guns early, the
 * strongest only late. An unbounded draw hands out Tempest Crown at t=22s
 * and leaves nothing to escalate to.
 *
 * Both sources of weapons go through this — the ARMS gate and the field
 * drop. The drop used to roll freely across the whole library, which is
 * why the top gun could arrive less than halfway through a chapter no
 * matter what the gate did.
 */
function weaponCeiling(s: RunState): number {
  const cfg = s.config;
  const top = cfg.weapons.length - 1;

  // Bounded by CHAPTER first. Without this the ladder only knew about
  // progress WITHIN a chapter, so chapter 1 ended with Tempest Crown --
  // tier 10, 240 damage against a starting gun's 9 -- and the finale boss
  // evaporated in 1.6 seconds at any HP it was given. The ten-gun library
  // is meant to span the ten chapters, not the first one.
  // Authored per level in chapters.csv, so "complexity rises" is a table
  // the designer edits rather than a curve buried in the sim.
  const chapterCap = Math.min(top, Math.max(1, cfg.weaponCap));

  // Then by progress within the chapter, so the band is walked rather than
  // handed over at the first gate.
  const waves = cfg.waves.length > 0 ? cfg.waves.length : 1;
  const progress = s.waveCursor / waves;
  const withinChapter = Math.floor(progress * chapterCap) + 1;

  const c = Math.min(chapterCap, withinChapter);
  return c > top ? top : c;
}

/**
 * Which gun to hand over: a seeded draw from the band between what is
 * held and what progress currently allows. Always strictly better than
 * the gun in hand, so a pickup can never read as a downgrade.
 */
function drawWeapon(s: RunState, stream: number): number {
  const top = s.config.weapons.length - 1;
  if (s.weaponIdx >= top) return s.weaponIdx;
  const ceiling = weaponCeiling(s);
  // Already at or beyond what progress allows: hand over NOTHING. Bumping
  // by one instead ("a gate should always do something") is a ratchet --
  // each pickup pushes one rung above the ceiling, and with the field
  // drops feeding it too the run races ahead of the chapter. Measured:
  // weapon 7 of 9 held at 90s, when the ceiling there is 5.
  if (ceiling <= s.weaponIdx) return s.weaponIdx;
  return s.weaponIdx + 1 + nextInt(s.rng, stream, ceiling - s.weaponIdx);
}

export function spawnEnemy(
  s: RunState, defIdx: number, x: number, y: number, asBoss = false, lane = 0,
): void {
  const idx = poolAlloc(s.ep);
  if (idx < 0) return; // Pool full: drop the spawn rather than grow.
  const def = s.config.enemies[defIdx]!;
  const tier = difficultyTier(s);
  const boss = asBoss || def.isBoss || def.isBeast;
  // PHYSICS LAW enforced at the CHOKEPOINT: every spawn from every
  // pattern, conveyor and future caller passes through here, so no body's
  // centre can start closer to a rail than the margin. Per-site clamps
  // proved fragile -- one was silently orphaned when the conveyor's
  // placement code evolved.
  const m = s.config.rules.wallMargin;
  if (x < m) x = m;
  else if (x > s.config.rules.arenaWidth - m) x = s.config.rules.arenaWidth - m;
  s.eX[idx] = x; s.eY[idx] = y;
  s.eLane[idx] = lane;
  // Bosses are authored at full strength; scaling them too would make the
  // finale swing wildly on how fast the player cleared the earlier waves.
  s.eHp[idx] = boss ? def.hp : def.hp * tier;
  s.eDefIdx[idx] = defIdx; s.eCooldown[idx] = 0;
  s.eIsBoss[idx] = boss ? 1 : 0;
  // bossIdx drives the FINALE presentation: the STORMHULK banner, the hp
  // bar, phase roars and the spawn tip. Beasts share the behaviour flag
  // above (hold the line, no tier scaling) but must never claim the
  // finale's identity -- a mid-run colossus wearing the STORMHULK banner
  // announces the boss three times per run and makes the real one noise.
  if (asBoss || def.isBoss) { s.bossIdx = idx; s.bossSpawned = true; }
  emit(s, Ev.Spawned, idx, defIdx, s.eX[idx], s.eY[idx]);
}

function spawnWavePortion(s: RunState, waveIdx: number, n: number): void {
  const w = s.config.waves[waveIdx]!;
  const r = s.config.rules;
  const topY = s.playerY + r.spawnAhead;
  for (let i = 0; i < n; i++) {
    const k = s.waveSpawned[waveIdx] + i;
    let x = r.arenaWidth / 2;
    let y = topY;
    /**
     * The lane this spawn is AUTHORED into -- stored on the enemy (see
     * `RunState.eLane`) and never re-derived from `eX` later, because `eX`
     * moves (D13 crossover, separation) and the authored lane must not.
     */
    let lane = 0;
    switch (w.spawnPattern) {
      case 'line_ahead': {
        const lanes = Math.max(1, r.laneCount);
        lane = w.lane >= 0 ? w.lane % lanes : w.waveIndex % lanes;
        const laneW = r.arenaWidth / lanes;
        x = lane * laneW + ((k + 0.5) / w.count) * laneW;
        break;
      }
      case 'arc_ahead': {
        // Lane-locked arc: bows within ONE lane instead of across the
        // corridor, so the other lane stays genuinely open.
        const lanes = Math.max(1, r.laneCount);
        lane = w.lane >= 0 ? w.lane % lanes : w.waveIndex % lanes;
        const laneW = r.arenaWidth / lanes;
        const t = (k + 0.5) / w.count - 0.5;
        x = lane * laneW + ((k + 0.5) / w.count) * laneW;
        // SPAWN LINE FIX (founder finding, 2026-08-25): the bow used to
        // subtract depth at its centre (up to 1.5 units nearer than
        // topY), so the middle of the arc appeared inside the corridor
        // rather than on the spawn line. Sign flipped -- the bow now
        // recedes AWAY from the player, never toward.
        y = topY + (0.25 - t * t) * 6;
        break;
      }
      case 'flank_pair': {
        // Both flanks of a SINGLE lane, not both walls of the corridor.
        const lanes = Math.max(1, r.laneCount);
        lane = w.lane >= 0 ? w.lane % lanes : w.waveIndex % lanes;
        const laneW = r.arenaWidth / lanes;
        x = lane * laneW + (k % 2 === 0 ? 0.22 : 0.78) * laneW;
        // SPAWN LINE FIX (founder finding, 2026-08-25): this subtracted
        // depth per pair, so a wave beyond the first two bodies spawned
        // progressively CLOSER than the far edge -- materialising inside
        // the visible corridor instead of walking in from it. Ranks now
        // recede FURTHER past topY as k grows, same convention
        // corridor_mass already uses, so nothing is ever born closer than
        // the spawn line.
        y = topY + Math.floor(k / 2) * 0.9;
        break;
      }
      case 'surround': {
        const lanes = Math.max(1, r.laneCount);
        lane = w.lane >= 0 ? w.lane % lanes : w.waveIndex % lanes;
        const laneW = r.arenaWidth / lanes;
        const step = Math.floor((k * DIR_STEPS) / Math.max(1, w.count)) % DIR_STEPS;
        x = lane * laneW + laneW * (0.5 + s.config.dirTable[step * 2]! * 0.42);
        // SPAWN LINE FIX (founder finding, 2026-08-25): dirTable's Y
        // component is signed (a direction around a circle), so roughly
        // half of every surround wave spawned BEHIND the far edge --
        // inside the corridor by construction, the pattern's whole point
        // being to appear on multiple sides at once. abs() keeps the
        // per-body depth VARIATION (some bodies sit further out than
        // others) while guaranteeing none of it goes negative.
        y = topY + Math.abs(s.config.dirTable[step * 2 + 1]!) * 3;
        break;
      }
      case 'boss_center': {
        // Was arenaWidth / 2 — with two lanes that is exactly the lane
        // BOUNDARY, and since fire is straight-only from a lane the boss
        // was very nearly unhittable: at squad 20 the stream spans
        // x in [0.64, 2.96] and a radius-1.35 boss spans [2.25, 4.95], so
        // only the outermost muzzles could ever connect. A boss now walks
        // ONE lane, and killing it means committing to that lane.
        // Lane is DRAWN, not derived from the wave index. Every boss wave
        // in chapter 1 happens to have an even index, so `waveIndex % lanes`
        // put all of them in lane 0 and a player camping lane 0 fought
        // every boss without ever making the decision the design is about.
        // Drawn from the waves stream, so it stays deterministic per seed
        // and unmemorisable across seeds.
        //
        // FORGE ISLES (founder mandate M-A): an AUTHORED lane wins over
        // the draw — the reward lane must never carry an enemy, the boss
        // included. The draw is consumed either way, so isles authored -1
        // keep their exact stream and their replays.
        const lanes = Math.max(1, r.laneCount);
        const drawn = nextInt(s.rng, Stream.Waves, lanes);
        lane = w.lane >= 0 ? w.lane % lanes : drawn;
        x = laneCenter(r.arenaWidth, lanes, lane);
        y = topY;
        break;
      }
      case 'dual_column': {
        // SPAWN LINE FIX (founder finding, 2026-08-25): this used to put a
        // column down EACH lane at once -- "the correct play is to stand
        // in the free lane and wait, so this pattern removes the free
        // lane" (its own prior rationale, preserved in git blame). That is
        // now a hard violation of the never-both-lanes rule, and there is
        // no way to keep this pattern's actual point (occupy every lane
        // simultaneously) while obeying it -- the point and the rule are
        // the same sentence with the polarity flipped. Confined to the
        // single `heavy` lane instead of also spawning `(heavy+1)%lanes`;
        // functionally a duplicate of corridor_mass now (single lane,
        // ranks receding past topY). Unused in every waves.csv row as of
        // this fix (grep: 0 of 436), so nothing currently authored changed
        // shape -- corrected so a future wave that DOES select it doesn't
        // inherit a pattern that can't be made compliant, rather than
        // leaving a live trap in the switch.
        const lanes = Math.max(1, r.laneCount);
        const laneW = r.arenaWidth / lanes;
        const heavy = w.waveIndex % lanes;
        lane = heavy;
        const laneX0 = lane * laneW;
        const perRank = Math.max(2, Math.floor(laneW / HORDE_FILE_GAP));
        const rank = Math.floor(k / perRank);
        const slot = k % perRank;
        const inThisRank = Math.min(perRank, w.count - rank * perRank);
        x = laneX0 + laneW * 0.5 + (slot - (inThisRank - 1) / 2) * (laneW / perRank);
        y = topY + rank * HORDE_RANK_GAP;
        break;
      }
      case 'corridor_mass': {
        // The horde advances as a COLUMN down a single lane, staggered in
        // ranks and receding toward the vanishing point. Which lane it
        // occupies alternates by wave, so the player is always reading
        // "which side is the wall of bodies on, and which side is open".
        // Spreading it across the whole corridor is what made lane choice
        // meaningless and the screen read as undifferentiated chaos.
        const lanes = Math.max(1, r.laneCount);
        lane = w.lane >= 0 ? w.lane % lanes : w.waveIndex % lanes;
        const laneW = r.arenaWidth / lanes;
        const laneX0 = lane * laneW;
        // RANK AND FILE. Ranks were offset by half a slot to look like a
        // crowd, but at a corridor's viewing angle a half-slot offset
        // reads as a diagonal drift rather than as a formation. Columns
        // now line up exactly behind one another, which is also what
        // makes "this column is in my lane" instantly legible.
        // PACKING IS THE POINT. At one body per 0.8 units a 3.6-wide lane
        // held four across, and four bodies in a row does not read as a
        // horde -- it reads as scattered singles. Shoulder-to-shoulder is
        // ~0.34 (the skitterer radius), and ranks half a body-depth apart,
        // which fills the lane wall-to-wall and many rows deep.
        const perRank = Math.max(2, Math.floor(laneW / HORDE_FILE_GAP));
        const rank = Math.floor(k / perRank);
        const slot = k % perRank;
        const inThisRank = Math.min(perRank, w.count - rank * perRank);
        x = laneX0 + laneW * 0.5 + (slot - (inThisRank - 1) / 2) * (laneW / perRank);
        // C1: DEPTH IS CONFIG, NOT A CONSTANT.
        //
        // This packed ranks 0.30 apart, which is shoulder-to-shoulder in X but
        // means a 195-body wave across 13 files is fifteen ranks — FOUR AND A
        // HALF UNITS deep. Measured at t30: 286 of 298 bodies inside one
        // 10-unit band, the entire near field empty. The mass had no depth for
        // any render pass to express, which is why render iteration could not
        // move it (L-06).
        //
        // Perspective does the packing work that a tight gap was trying to do:
        // at corridor viewing angles ranks a metre apart still overlap on
        // screen, so the column reads solid while actually occupying the
        // corridor. Config-driven because it is a balance quantity — spreading
        // arrival over depth spreads it over TIME, which the winrate sweep
        // gates (M2).
        //
        // ISLE-1 DENSITY FIX, REMOVED (founder finding, 2026-09-06): chapter
        // 1 used to derive rank depth from a constant SCREEN-space gap
        // (`isle1RankDepth`, a baked table) instead of this flat world-space
        // gap, to fix an extreme-depth smear at 1700-body waves needing
        // ~100 ranks. Those wave counts are gone (split into chapter-12-
        // range waves the same session); chapter 1's deepest rank now
        // matches chapter 12's exactly (rank 11, ~104u), so the curve had
        // no remaining problem to solve -- it was buying rank-boundary
        // legibility nobody asked for at the cost of the packed-mass look
        // the flat gap already produces correctly for every other chapter.
        // Chapter 1 now uses the same formula as every other chapter.
        //
        // TOPY MOVED TO THE INVISIBLE EDGE (founder finding, 2026-09-06):
        // every other pattern above still spawns at topY (playerY +
        // spawnAhead, ~60-90u) -- close enough to be born already inside
        // or at the edge of frame. corridor_mass is the pattern that
        // reads as "the horde", so it alone now originates at
        // DECK_INVISIBLE_EDGE_UNITS (240u, past the deck's own far-edge
        // fade) -- a wave's ranks stream INTO frame over the walk from
        // there, never materialise already on the spawn line. Timing
        // (w.startTick/spawnDurationTicks) is untouched: only WHERE a
        // rank is born moves, not WHEN.
        y = s.playerY + DECK_INVISIBLE_EDGE_UNITS + rank * r.waveRankGap;
        break;
      }
      case 'full_block': {
        // A BLOCK, NOT A STREAM (founder finding, 2026-08-25). corridor_mass
        // packs a column into ONE lane by design (lane choice has to mean
        // something); this pattern is the deliberate exception -- W bodies
        // across the WHOLE road, D ranks deep, spawned in one tick. Lane is
        // DERIVED per-body from its spawned x (laneOf), not drawn or
        // authored, so the existing per-lane convergence/crossover movement
        // code downstream treats each column exactly as it would an
        // ordinary same-lane body once the block closes to contact range --
        // no new movement logic needed, only new spawn geometry.
        const perRank = Math.max(2, Math.floor(r.arenaWidth / HORDE_FILE_GAP));
        const rank = Math.floor(k / perRank);
        const slot = k % perRank;
        const inThisRank = Math.min(perRank, w.count - rank * perRank);
        x = r.arenaWidth * 0.5 + (slot - (inThisRank - 1) / 2) * (r.arenaWidth / perRank);
        // ISLE-1 DENSITY FIX, REMOVED here too (see corridor_mass, same file,
        // same date, 2026-09-06): full_block now uses the same flat gap as
        // every chapter. full_block is unauthored anywhere in waves.csv as
        // of this change, so nothing currently authored changes shape.
        y = topY + rank * r.waveRankGap;
        lane = laneOf(r.arenaWidth, Math.max(1, r.laneCount), x);
        break;
      }
    }
    // PACKED AND SCATTERED (founder, 2026-08-31): the 2026-08-25 finding
    // above (exact ranks, no jitter) was reacting to a SYSTEMATIC per-rank
    // half-slot offset -- every other rank shifted the same fixed amount,
    // which reads as the whole column drifting diagonally, not as a loose
    // crowd. That is a different thing from PER-BODY random jitter, which
    // has no systematic direction to read as drift. This draw was already
    // being made every tick and thrown away (`// keep the draw, drop the
    // effect`) purely to preserve RNG-stream parity with chapters that
    // still apply it -- applying it here keeps the same rank/file grid
    // (still packed, still a readable wall) while breaking the perfectly
    // uniform lattice the grid alone produces (now scattered within it).
    // Y/depth is left untouched -- that axis is the carefully-measured
    // packing/pace logic right above (waveRankGap, same for every chapter)
    // and jittering it risks undoing that.
    // boss_center still draws nothing at all -- the boss spawns exactly
    // centred, on purpose, unchanged from before this fix.
    if (w.spawnPattern !== 'boss_center') {
      x += nextRange(s.rng, Stream.Waves, -0.03, 0.03);
    }
    if (x < 0.2) x = 0.2;
    if (x > r.arenaWidth - 0.2) x = r.arenaWidth - 0.2;
    // PHYSICS LAW: patterns and jitter can place a body's CENTRE near the
    // deck edge, which puts its shoulders inside the rail. One clamp here
    // covers every pattern, present and future.
    if (x < r.wallMargin) x = r.wallMargin;
    if (x > r.arenaWidth - r.wallMargin) x = r.arenaWidth - r.wallMargin;
    spawnEnemy(s, w.enemyIdx, x, y, w.spawnPattern === 'boss_center', lane);
  }
  s.waveSpawned[waveIdx] += n;
}

function rebuildGrid(s: RunState): void {
  s.cellHead.fill(-1);
  const { cellSize, gridCols, gridRows } = s;
  for (let i = 0; i < s.ep.cap; i++) {
    if (!s.ep.alive[i]) continue;
    let cx = Math.floor(s.eX[i] / cellSize) + 1;
    let cy = Math.floor((s.eY[i] - s.playerY + 8) / cellSize) + 1;
    if (cx < 0) cx = 0; if (cx >= gridCols) cx = gridCols - 1;
    if (cy < 0) cy = 0; if (cy >= gridRows) cy = gridRows - 1;
    const cell = cy * gridCols + cx;
    s.cellNext[i] = s.cellHead[cell];
    s.cellHead[cell] = i;
  }
}

export function stepRun(s: RunState, input: TickInput): void {
  const cfg = s.config;
  const r = cfg.rules;

  // DEATH IN PLACE (Contact and Impact, "squad bodies" part 2): the
  // position of the last COMBAT blow this tick, read by the formation
  // section below to decide which soldier index dies. Only a genuine hit
  // -- enemy contact, enemy projectile, a breach -- sets this; a toll-style
  // squad cost (barrier ram, gate ram, monolith) has no attacker and
  // leaves it NaN, falling back to plain index order. If more than one
  // blow lands in a tick, the last one written wins -- combat cooldowns
  // make more than one an edge case, not the common path.
  let blowX = NaN, blowY = NaN;

  // The run never pauses. There is no choice modal and therefore no
  // awaiting-choice state: boons apply the instant they are earned (see
  // step 15). `quit` is the only command, and it ends the run.
  if (input.command?.k === 'quit') {
    s.outcome = Outcome.Quit;
    s.phase = Phase.Ended;
    return;
  }

  if (s.phase === Phase.Ended) return;

  // --- 3. advance -----------------------------------------------------------
  s.tick++;
  s.events.length = 0;

  // --- 4. input -------------------------------------------------------------
  const dir = input.dir % DIR_STEPS;
  const mag = input.mag > MAG_MAX ? MAG_MAX : input.mag;
  if (mag > 0) {
    const speed = (s.statMoveSpeed * mag) / MAG_MAX / TICK_RATE;
    s.playerX += cfg.dirTable[dir * 2]! * speed;
    s.playerY += cfg.dirTable[dir * 2 + 1]! * speed;
    // PHYSICS LAW: the margin is a body half-width, so the formation's
    // outer shoulders stay off the rails -- a 0.5 centre-clamp let the
    // edge soldiers walk visibly INTO the kerb.
    if (s.playerX < r.wallMargin) s.playerX = r.wallMargin;
    if (s.playerX > r.arenaWidth - r.wallMargin) s.playerX = r.arenaWidth - r.wallMargin;
    if (s.playerY < 0.5) s.playerY = 0.5;
    if (s.playerY > r.arenaHeight - 0.5) s.playerY = r.arenaHeight - 0.5;
  }

  // NO snap-to-lane-centre on release. Teleporting the squad to a lane
  // centre the moment the finger stopped is what made movement read as
  // tap-to-lane instead of as a drag: the squad went where it wanted, not
  // where the thumb was. Lanes remain the structure the HORDE spawns in;
  // the player moves freely across the corridor.

  // A deployed vehicle burns down every tick; stats recompute on the
  // frame it expires so its firepower leaves exactly when it does.
  if (s.vehicleTicks > 0) {
    s.vehicleTicks--;
    if (s.vehicleTicks === 0) recomputeStats(s);
  }

  // --- 4b. dodge (A-43: the one added player verb) --------------------------
  // Both timers count down every tick regardless of whether a dodge is
  // requested this tick, same pattern vehicleTicks uses above. The
  // invulnerability itself lives in damageSquad (below) as a single early
  // return -- full, not partial, because a partial dodge gives the player
  // no clean read of whether the verb worked.
  if (s.dodgeActiveTicks > 0) s.dodgeActiveTicks--;
  if (s.dodgeCooldownTicks > 0) s.dodgeCooldownTicks--;
  if (input.command?.k === 'dodge' && s.dodgeCooldownTicks <= 0) {
    s.dodgeActiveTicks = Math.round(r.dodgeActiveSec * TICK_RATE);
    s.dodgeCooldownTicks = Math.round(r.dodgeCooldownSec * TICK_RATE);
    emit(s, Ev.Dodge, -1);
  }

  // --- 5. scroll ------------------------------------------------------------
  // The run ACCELERATES as it escalates. A curve that only adds bodies
  // gets busier without getting tenser; speed is what turns "more enemies"
  // into adrenaline. Capped, because past human reaction time a ramp stops
  // being difficulty and becomes a coin flip.
  const ramp = Math.min(r.speedRampMax, 1 + s.waveCursor * r.speedRampPerWave);
  s.worldY += (r.scrollSpeed * ramp) / TICK_RATE;

  // --- 6. waves -------------------------------------------------------------
  for (let wi = s.waveCursor; wi < cfg.waves.length; wi++) {
    const w = cfg.waves[wi]!;
    if (w.startTick > s.tick) break;
    const dur = w.spawnDurationTicks;
    const elapsed = s.tick - w.startTick + 1;
    const target = dur <= 0 ? w.count : Math.min(w.count, Math.ceil((w.count * elapsed) / dur));
    const due = target - s.waveSpawned[wi];
    if (due > 0) spawnWavePortion(s, wi, due);
    if (s.waveSpawned[wi] >= w.count && wi === s.waveCursor) s.waveCursor++;
  }

  // --- 6b. the conveyor -----------------------------------------------------
  // A continuous flow of bodies underneath the authored waves, so the field
  // is NEVER empty: authored waves are the spikes and set-pieces, the flow
  // is the baseline the whole game stands on. It accelerates linearly with
  // time -- pace is the difficulty curve -- and is capped so the late game
  // stays a fight rather than a flood beyond any possible kill rate.
  {
    // THE PREFILL. rules.csv has claimed since v3 that "the lane is FULL
    // from second one", but a conveyor starting from an empty field puts a
    // dozen bodies on the spawn line at second one and nothing anywhere
    // else -- the mechanism behind the claim never existed, and the empty
    // opening is the only opening most players see. This spawns the
    // standing crowd the conveyor would have built if it had already been
    // running, spread over [30, depth]: near enough to be IN the frame and
    // shootable from second one, far enough (probed: near edges under 30
    // arrive before one rifle can answer) not to overrun a squad of one.
    // tick has already been incremented this step (top of stepRun), so the
    // FIRST tick a run ever executes arrives here as tick === 1. Guarding
    // on 0 made the whole block dead code — caught because the step-2
    // measurement came back bit-identical to step 1.
    if (s.tick === 1 && r.flowPrefillSec > 0) {
      const lanes = Math.max(1, r.laneCount);
      const laneW = r.arenaWidth / lanes;
      const nearEdge = 30;
      // FAR EDGE MOVED TO THE DECK'S UNDIMMED TERMINUS (founder finding,
      // 2026-09-06): this used to stop at r.spawnAhead (60-90u, wherever
      // this chapter's waves spawn) -- correct for "off the spawn line"
      // but nowhere near "fills the corridor", since the deck stays fully
      // visible for another 138-168u past that. The prefill's whole job is
      // to paint what the conveyor would already have built if it had been
      // running -- that job isn't done until it reaches as far as the eye
      // can actually see.
      const span = Math.max(1, DECK_UNDIMMED_TERMINUS_UNITS - nearEdge);
      const perRank = Math.max(2, Math.floor(laneW / HORDE_FILE_GAP));
      // DEPTH RESOLUTION, NOT ARRIVAL COUNT (founder finding, 2026-09-06):
      // `kk = floor(k / depthLanes)` below spends one depth-step of
      // resolution per `depthLanes` bodies, so the total body count needed
      // to reach a given depth is `ranks * perRank * depthLanes` -- fixed
      // by the depth this loop needs to reach, not a free density knob.
      // A chapter with two live lanes (Rules.hordeLane < 0) needs each
      // lane independently painted to depth, hence `lanes`; a chapter that
      // pins its whole horde into one physical lane (hordeLane >= 0, isle
      // 1 today) has only one lane to paint -- charging it for a second,
      // unrendered virtual lane would spend HALF the enemy pool on bodies
      // that land exactly on top of others already placed (same rank, same
      // file, `s.flowSeq`'s own cycle repeating), for zero additional
      // depth covered.
      const depthLanes = r.hordeLane >= 0 ? 1 : lanes;
      // RANK PITCH: the same `waveRankGap` corridor_mass/dual_column pack
      // their own ranks at (config, not a constant -- C1 in corridor_mass's
      // own comment) -- not HORDE_FILE_GAP's shoulder-to-shoulder 0.26,
      // which is an X-axis (file) pitch, and not literally solid at every
      // depth unit either: corridor_mass's own established finding is that
      // corridor-camera perspective already reads ranks a metre apart as
      // one continuous mass, so packing any tighter buys triangles no eye
      // can resolve without also buying real gameplay depth.
      const ranks = Math.max(1, Math.ceil(span / r.waveRankGap));
      const n = ranks * perRank * depthLanes;
      for (let k = 0; k < n; k++) {
        // Same weighted roster draw as the conveyor below, same stream.
        let total = 0;
        for (const d of cfg.enemies) if (!d.isBoss && !d.isBeast) total += d.spawnWeight;
        let roll = nextFloat(s.rng, Stream.Waves) * total;
        let defIdx = 0;
        for (let i = 0; i < cfg.enemies.length; i++) {
          const d = cfg.enemies[i]!;
          if (d.isBoss || d.isBeast) continue;
          roll -= d.spawnWeight;
          if (roll <= 0) { defIdx = i; break; }
        }
        // Concentrated exactly as the tick-0 conveyor would be: lane 0 hot.
        // FORGE (founder mandate M-A): on an isle with a reward lane, the
        // ENTIRE prefill floods the horde lane — the reward lane never
        // carries an enemy. The hot-share draw is consumed either way so
        // non-forge isles keep their exact stream.
        const inHot = nextFloat(s.rng, Stream.Waves) < r.flowHotShare;
        // HORDE LANE PIN wins over both: a chapter whose every authored
        // wave already commits to one lane (Rules.hordeLane) has nothing
        // left to decide here.
        const lane = r.hordeLane >= 0 ? r.hordeLane
          : r.rewardLane >= 0
          ? (r.rewardLane === 0 ? 1 : 0) % lanes
          : inHot ? 0 : 1 % lanes;
        const file = s.flowSeq++ % perRank;
        const x = lane * laneW + HORDE_FILE_GAP * 0.5 + file * HORDE_FILE_GAP
          + nextRange(s.rng, Stream.Waves, -0.04, 0.04);
        // A COLUMN MID-MARCH, not confetti. The fiction the prefill exists
        // to serve: the invasion was already walking this road before the
        // player arrived, so the standing crowd reads as the same army the
        // conveyor feeds in at the horizon.
        //
        // CONTINUOUS RANKS, PLATOONS REMOVED (founder finding, 2026-09-06):
        // the previous marching-platoon design (two packed ranks, then a
        // wide gap, repeating) was meant to look like strung-out columns,
        // but at the old n=36/span=30 it never produced more than ONE
        // platoon -- the gap logic never actually ran, and the "filled"
        // corridor was two ranks deep with everything past it empty, which
        // is the exact defect this fix exists to close. Ranks now pack
        // back-to-back for the whole span, same as corridor_mass.
        const kk = Math.floor(k / depthLanes);
        const rank = Math.floor(kk / perRank);
        // NOT TOUCHED (founder finding, 2026-08-25): this prefill, not
        // corridor_mass, is what actually populates the t=4s density
        // measurement the founder measured (corridor_mass's own first big
        // wave doesn't start until t=5s) -- confirmed by measuring before/
        // after the corridor_mass fix below and finding ZERO change at
        // t=4s/seed=1000. Tried applying the same derived-screen-Y-delta
        // treatment here too (per-rank, then per-body granularity, both
        // committed to a scratch branch and measured, not just reasoned
        // about): both made distinct-bands WORSE (14->11, then 14->9) and
        // occluding-pairs worse (99->110, then ->134). Root cause: this
        // mechanism's own spawn depth (nearEdge=30) sits so close to the
        // horizon under the current camera (footY(30)=0.053, and the
        // horizon itself is ~0 at this pitch) that the whole nearEdge..
        // spawnAhead range holds under 0.05 of screen budget -- nowhere
        // near enough room for a 20px-per-rank target, so ranks got pushed
        // to extreme world depths that then re-converged after 4 seconds
        // of independent per-body movement (enemy types close at
        // different speeds). Left exactly as authored rather than ship an
        // unverified change with a measured regression; scoped to
        // corridor_mass (below) as literally asked ("per-WAVE depth
        // placement"), and reported as a known gap for chapter 1's t=4s
        // reading specifically, not silently patched over.
        const y = s.playerY + nearEdge
          + Math.min(span, rank * r.waveRankGap)
          + nextFloat(s.rng, Stream.Waves) * 0.25;
        spawnEnemy(s, defIdx, x, y, false, lane);
      }
    }
    const sec = s.tick / TICK_RATE;
    const rate = Math.min(r.flowMaxPerSec, r.flowStartPerSec + sec * r.flowRampPerSec2);
    // ONE STIMULUS AT A TIME (beat grammar, per-chapter q_mute): while a
    // question is on the table — a gate pair inside the commit window —
    // the conveyor holds its fire. The accumulator keeps charging, so the
    // held bodies arrive as a denser burst AFTER the answer, which is the
    // beat's payoff shape falling out of the mute for free. Deterministic:
    // gate positions are seeded state like everything else.
    let questionLive = false;
    if (r.flowQuestionMute) {
      for (let gi2 = 0; gi2 < s.gp.cap; gi2++) {
        if (!s.gp.alive[gi2]) continue;
        const gdy2 = s.gY[gi2] - s.playerY;
        if (gdy2 > 0 && gdy2 < 20) { questionLive = true; break; }
      }
    }
    s.flowAcc += rate / TICK_RATE;
    if (questionLive && s.flowAcc > 3) s.flowAcc = 3;
    while (!questionLive && s.flowAcc >= 1) {
      s.flowAcc -= 1;
      // Weighted draw across the ordinary roster (never bosses or beasts),
      // on the waves stream so replays are unaffected by pool occupancy.
      let total = 0;
      for (const d of cfg.enemies) if (!d.isBoss && !d.isBeast) total += d.spawnWeight;
      let roll = nextFloat(s.rng, Stream.Waves) * total;
      let defIdx = 0;
      for (let i = 0; i < cfg.enemies.length; i++) {
        const d = cfg.enemies[i]!;
        if (d.isBoss || d.isBeast) continue;
        roll -= d.spawnWeight;
        if (roll <= 0) { defIdx = i; break; }
      }
      // The conveyor CONCENTRATES in one lane at a time, alternating on a
      // slow cycle, with a light spill into the other.
      //
      // Spread evenly across the corridor it was unwinnable by
      // construction: the curtain of fire covers ONE lane, so half the
      // flow could never be shot at all -- measured as a kill rate of
      // ~31/s against an arrival rate double that, with the squad ground
      // to nothing by leaks it had no way to prevent. Concentrating gives
      // the player a hot lane they can actually hold, and keeps the lane
      // choice meaningful instead of decorative.
      const lanes = Math.max(1, r.laneCount);
      const laneW = r.arenaWidth / lanes;
      // THE SPATIAL DILEMMA, made permanent. While a prize machine is on
      // the field, the flow concentrates in the OTHER lane -- the machine
      // occupies one lane, the horde floods the opposite one, always. The
      // choice is never "which lane is empty" but "pay the flooded lane's
      // price to win the machine, or hold the flood and pass the prize".
      // Only with no machine on the field does the hot lane go back to the
      // slow timed cycle.
      let machineLane = -1, machineDy = 1e9;
      for (let gi = 0; gi < s.gp.cap; gi++) {
        if (!s.gp.alive[gi]) continue;
        const op = s.gOp[gi];
        if (op !== GATE_BARRIER && op !== GATE_CRATE && op !== GATE_VEHICLE
          && op !== GATE_ALLY && op !== GATE_ARMOUR) continue;
        const dy = s.gY[gi] - s.playerY;
        if (dy > 0 && dy < machineDy) { machineDy = dy; machineLane = s.gLane[gi]; }
      }
      const hot = machineLane >= 0
        ? (machineLane + 1) % lanes
        : Math.floor(s.tick / (TICK_RATE * r.flowLaneHoldSec)) % lanes;
      const inHot = nextFloat(s.rng, Stream.Waves) < r.flowHotShare;
      // HORDE LANE PIN (see the prefill's own comment above, and
      // Rules.hordeLane): this background flow used to spill a steady
      // `1 - flowHotShare` trickle into the COLD lane every tick,
      // regardless of what every authored wave in the chapter already
      // committed to -- on isle 1 that cold lane is the RESERVED REWARD
      // LANE, which was never supposed to carry an enemy at all, and it
      // is the reason a "single-lane" chapter still read as two lanes
      // worth of traffic on screen. The hot-share draw is still consumed
      // so unpinned chapters keep their exact stream.
      const lane = r.hordeLane >= 0 ? r.hordeLane : inHot ? hot : (hot + 1) % lanes;
      // PACKED, not sprinkled. The conveyor used to scatter each body
      // anywhere across an 8-unit depth band, so the baseline flow arrived
      // as loose stragglers with daylight between them -- the opposite of
      // the wall-of-bodies the corridor is built to be fought from. Bodies
      // now land on the same rank grid the authored waves use, which packs
      // them shoulder to shoulder and file to file.
      const perRank = Math.max(2, Math.floor(laneW / HORDE_FILE_GAP));
      const n = s.flowSeq++;
      const file = n % perRank;
      // 26 ranks, not 6. Packing tightly in X is the CARPET look; packing
      // tightly in Y as well pulled the whole flow ~6 units closer to the
      // squad and stole that much reaction time -- measured, isle 1 went
      // from 69% to 0% on nothing but this. Depth spread must match the
      // 8-unit band the scatter used to cover.
      const rank = Math.floor(n / perRank) % 26;
      const x = lane * laneW + HORDE_FILE_GAP * 0.5 + file * HORDE_FILE_GAP
        + nextRange(s.rng, Stream.Waves, -0.04, 0.04);
      // SPAWN BEYOND THE INVISIBLE EDGE (founder finding, 2026-09-06): this
      // used to spawn at r.spawnAhead (~60-90u, the offscreen-enough floor
      // MIN_SPAWN_DEPTH_OFFSCREEN in config.ts was built for), which pops a
      // body into a visible-but-far part of frame every tick. The deck's
      // own fade zone (DECK_INVISIBLE_EDGE_UNITS, 240u) is genuinely
      // invisible -- spawning there means every arrival is a walk-in from
      // nothing, never a pop, and the ~46s extra walk at HORDE_SPEED this
      // adds is the visible "streaming down continuously" the corridor is
      // supposed to read as, not a timing regression to fix.
      const y = s.playerY + DECK_INVISIBLE_EDGE_UNITS + rank * HORDE_RANK_GAP
        + nextRange(s.rng, Stream.Waves, 0, 0.25);
      spawnEnemy(s, defIdx, x, y, false, lane);
    }
  }

  // --- 6b1. raise scheduled monoliths ---------------------------------------
  // s.monolithRaiseTick[i] (state.ts, drawn once at init from a seeded
  // +/-0.35s jitter) is the actual raise tick -- never the config's raw
  // `m.atTick` directly, so the schedule isn't the same metronome every
  // seed while staying fully deterministic per seed.
  for (let i = 0; i < cfg.monoliths.length; i++) {
    if (s.monolithRaiseTick[i] !== s.tick) continue;
    const m = cfg.monoliths[i]!;
    spawnMonolith(s, m.lane, m.hp, m.titan, m.lifeSec,
      BLOCK_OPS[m.payload] ?? BLOCK_TITAN, m.value);
  }

  // --- 6b2. monoliths --------------------------------------------------------
  // They drift back far more slowly than the corridor scrolls, so a rock
  // stays ALONGSIDE the squad for the better part of a minute instead of
  // sweeping past in the seconds a gate gets. That persistence is the
  // whole mechanic: the choice is not "do I enter this lane", it is "how
  // much of the next forty seconds of fire do I spend on it".
  for (let i = 0; i < s.mp.cap; i++) {
    if (!s.mp.alive[i]) continue;
    s.mY[i] -= (r.scrollSpeed * 0.22) / TICK_RATE;
    s.mLife[i]--;
    // ISLE-12 REBUILD (founder finding, 2026-08-25): "arriving before it
    // breaks costs the player" -- a race monolith is a genuine race, not
    // an opportunity cost like every other monolith payload (below,
    // unchanged for them). It occupies the FULL road width (no lane to
    // dodge into), so "arrives" means its own depth footprint reaches the
    // squad's Y. The WIN branch (breaking it) already frees the pool slot
    // from the projectile-chip loop before this ever runs, so reaching
    // here with a race monolith always means the loss branch. One
    // soldier -- the same currency every other contact in this game costs
    // (see damageSquad's other call sites) -- not scaled by R_i, so
    // losing the race is a normal miss, not a spike.
    if (s.mOp[i] === BLOCK_RACE) {
      if (s.mY[i] < s.playerY + MONOLITH_HALF_D) {
        damageSquad(s, unitHp(s));
        poolFree(s.mp, i); continue;
      }
      if (s.mLife[i] <= 0) { poolFree(s.mp, i); continue; }
      continue;
    }
    // Left behind: the prize is simply lost. There is NO penalty for
    // ignoring a monolith -- pure opportunity cost is what makes spending
    // fire on one a real decision rather than a toll.
    //
    // On a FORGE isle the rock stands ON the road, so it must expire
    // while still AHEAD of the squad (+6u) — drifting through the
    // formation would put a boulder inside soldiers' bodies (physics
    // law). Off-road rocks keep the old behind-the-line expiry.
    const gone = r.rewardLane >= 0 ? s.mY[i] < s.playerY + 6 : s.mY[i] < s.playerY - 6;
    if (s.mLife[i] <= 0 || gone) { poolFree(s.mp, i); continue; }
  }

  // --- 6c. allies -----------------------------------------------------------
  // The monster advances INTO the horde and clears what it reaches. It is
  // deliberately not a second squad: it holds one lane, it cannot be
  // steered, and it dies. Its whole job is to be the visible proof that
  // what you charged became real.
  for (let i = 0; i < s.lp.cap; i++) {
    if (!s.lp.alive[i]) continue;
    s.lY[i] += 2.2 / TICK_RATE;
    // It never outruns the corridor it is defending.
    const front = s.playerY + r.spawnAhead * 0.6;
    if (s.lY[i] > front) s.lY[i] = front;
    if (s.lCooldown[i] > 0) s.lCooldown[i]--;
    const rad = s.lRadius[i];
    let struck = false;
    for (let e = 0; e < s.ep.cap; e++) {
      if (!s.ep.alive[e]) continue;
      const edef = cfg.enemies[s.eDefIdx[e]]!;
      const dx = s.eX[e] - s.lX[i];
      const dy = s.eY[e] - s.lY[i];
      // Combined-radii contact, same as every other body-vs-body test in
      // this file (player-vs-enemy, projectile-vs-enemy). This one had been
      // testing the ally's own radius alone, so a large-bodied target --
      // the boss included -- had to overlap the ally's CENTRE before either
      // touched: the boss's own 1.9u radius was silently missing from the
      // window, which is exactly backwards for a body big enough to be seen
      // from across the lane.
      const rr = rad + edef.radius;
      if (dx * dx + dy * dy > rr * rr) continue;
      // Bodies grind it down whether or not its swing is ready, so an ally
      // parked in a flood dies to the flood -- it is a tide-turner, not an
      // invulnerable wall the player can hide behind.
      s.lHp[i] -= edef.contactDamage / TICK_RATE;
      if (s.lCooldown[i] > 0) continue;
      struck = true;
      s.eHp[e] -= s.lPower[i];
      s.damageDealt += s.lPower[i];
      if (s.eHp[e] <= 0) { s.eHp[e] = 0; killEnemy(s, e, DeathCause.Blow); }
      else updateBossPhase(s, e);
    }
    if (struck) s.lCooldown[i] = Math.round(TICK_RATE * 0.55);
    if (s.lHp[i] <= 0) {
      emit(s, Ev.Ally, i, 1);
      poolFree(s.lp, i);
    }
  }

  // --- 7. spatial hash ------------------------------------------------------
  rebuildGrid(s);

  // --- 8. enemies -----------------------------------------------------------
  // Tick-invariant: the player doesn't move again until this whole loop is
  // done, so this is computed once rather than per enemy.
  const playerLane = laneOf(r.arenaWidth, Math.max(1, r.laneCount), s.playerX);
  for (let i = 0; i < s.ep.cap; i++) {
    if (!s.ep.alive[i]) continue;
    const def = cfg.enemies[s.eDefIdx[i]]!;
    const dx = s.playerX - s.eX[i];
    const dy = s.playerY - s.eY[i];
    const dist = Math.sqrt(dx * dx + dy * dy);

    // The horde marches STRAIGHT down its lane. Homing on the player made
    // every body drift sideways across lane boundaries, which destroyed
    // the one thing lanes exist for: a lane you can see is clear must
    // STAY clear. Speed ramps more gently than hp, because a horde that
    // outruns human reaction time is a coin flip, not a difficulty curve.
    // ONE HORDE, ONE PACE. Every ordinary body now walks at the SAME speed,
    // set by the roster's baseline rather than per type. Formations were
    // being spawned correctly -- shoulder to shoulder, rank behind rank --
    // and then dissolving within about two seconds because a skitterer
    // (4.6/s) outran a brute (3.1/s) in the same rank. That drift, not the
    // spawn placement, is why the horde read as scattered singles instead
    // of the advancing wall the reference shows. Beasts and the finale keep
    // their own authored pace: they are meant to break the line.
    // REVERTED (founder finding, 2026-08-25): a per-body speed ramp lived
    // here for one deploy -- every ordinary body crawled in at
    // ARRIVAL_MIN_SPEED_FRAC and accelerated continuously to contact,
    // visible rubber-banding, and ungated (every chapter, not just isle
    // 12's blocks). A body's speed is constant for its whole approach
    // again; arrival spread for `full_block` waves now comes from
    // RELEASE TIMING (spawn_duration_sec on the wave row), not motion.
    // F4 AUDIT: per-chapter bossSpeedMult (1 everywhere it isn't
    // overridden) closes the boss/beast transit gap without moving every
    // other chapter's finale pacing -- see ChapterDef.bossSpeedMult.
    // PACE AUDIT: per-chapter hordeSpeedMult (1 everywhere it isn't
    // overridden) scales the FLOOR fodder starts at; difficultyTier's own
    // ramp still multiplies on top, so the chapter still escalates.
    const spd = s.eIsBoss[i] === 1 ? def.speed * r.bossSpeedMult
      : HORDE_SPEED * r.hordeSpeedMult * (1 + (difficultyTier(s) - 1) * 0.35);
    s.eY[i] -= spd / TICK_RATE;

    // SOLID MEANS SOLID: no enemy may occupy a live monolith's own ground.
    // First attempted against chapter 2 (now out of scope) and reverted --
    // see tasks/lessons.md L-25 for the full account of why. Re-scoped to
    // isles 1 and 12 only, on their own numbers: isle 1's monoliths are
    // all authored into lane 1 while its horde is pinned to lane 0 (see
    // Rules.hordeLane), so they never share ground and this is a no-op
    // there. Isle 12 has 5 monoliths in the horde's own lane (0), so the
    // shoulder-past design applies there for real -- the failure mode
    // that broke chapter 2 (a deflected mass landing on the lane BOUNDARY
    // trips the D13 crossover's proximity check when the player is
    // standing in the OTHER lane) requires the player to actually be
    // parked in that other lane, and isle 12's own bot logic (reference-
    // policy.ts) keeps the squad committed to lane 0 whenever it carries
    // live threat, which for isle 12 is effectively always -- confirmed
    // empirically, not assumed (see the session's winrate + browser
    // capture verification). A rock fills most (3.38 of 3.6u) of its
    // lane but not all of it; the body is shouldered to just past its
    // edge (own radius included in the margin, Iron Rule 8's body-half-
    // width clause) and keeps walking the whole time -- forward progress
    // never stops, so there is nothing to queue and nothing to release,
    // and the body still never once occupies the rock's ground. `eLane`
    // (breach pricing, D13 crossover) is untouched: this only moves `eX`.
    for (let mi = 0; mi < s.mp.cap; mi++) {
      if (!s.mp.alive[mi]) continue;
      const mx = s.mX[mi];
      if (Math.abs(s.eY[i] - s.mY[mi]) > MONOLITH_HALF_D) continue;
      const halfW = MONOLITH_HALF_W + def.radius;
      const off = s.eX[i] - mx;
      if (Math.abs(off) >= halfW) continue;
      s.eX[i] = mx + (off < 0 ? -halfW : halfW);
      if (s.eX[i] < r.wallMargin) s.eX[i] = r.wallMargin;
      if (s.eX[i] > r.arenaWidth - r.wallMargin) s.eX[i] = r.arenaWidth - r.wallMargin;
    }

    // THEY CHASE. Marching straight down a lane, a body walked PAST the
    // squad and out of the run without ever fighting it, which is why the
    // horde read as traffic rather than as an enemy coming for you.
    //
    // The convergence is deliberately LATE -- only inside the last few
    // units -- because a lane you can see is clear must STAY clear. That is
    // the one property the entire lane decision rests on. What changes is
    // that once a body is upon you it comes FOR you rather than beside you.
    if (s.eIsBoss[i] !== 1) {
      const cdy = s.eY[i] - s.playerY;
      const cdx0 = s.playerX - s.eX[i];
      // ONLY BODIES ALREADY IN YOUR LANE CHASE.
      //
      // Letting every body converge from anywhere was a mistake with a
      // consequence I did not see until it was measured: the far lane
      // stopped leaking and started FEEDING the squad, because its bodies
      // walked into the curtain of fire instead of past it. That removed
      // the entire cost of standing in the reward lane, and a reward-
      // ignoring player then measured identically to a reward-taking one --
      // same end weapon, same peak squad, same win rate. The dilemma the
      // whole game rests on had been quietly deleted by a movement tweak.
      //
      // A body in the lane you are holding comes for you. A body in the
      // lane you abandoned walks past and breaches, and now costs a whole
      // soldier when it does. That is what makes lane choice cost anything.
      // D13 CROSSOVER (founder ruling 2026-08-17, docs/ART_BIBLE.md M-A
      // note; landed value docs/FRAME_SPEC.md §11): a chance-per-enemy
      // fraction of bodies entering close range converge fully on the
      // player's X; the rest behave exactly as before D13. All(share=1)/
      // none(share=0)/partial-lunge were tried and measured -- only a
      // per-enemy binary split at 0.15 cleared the winrate floor under the
      // (then-stationary) reference policy. That 0.15 was hardcoded as
      // module constant CROSSOVER_SHARE (no lane awareness at all -- ANY
      // nearby enemy could roll it, regardless of which lane the player
      // was standing in) and is now `rules.csv` `converge_chance`, read
      // once at `initRun` (config.ts). LANE-GATED (this session, isle-12
      // report finding): the old check let a body cross INTO a lane the
      // player never entered, which emptied every authored chapter-12
      // wave of any real threat -- the player's own current lane vs. this
      // body's AUTHORED lane (`s.eLane[i]`, never the body's live `eX`,
      // which D13 itself moves) now decides which chance applies:
      // same-lane keeps the original 0.15 (`converge_chance`); cross-lane
      // is `cross_lane_converge_chance`, defaulted to 0 -- a body may not
      // cross a lane it was never authored into unless a chapter opts in.
      const laneW = r.arenaWidth / Math.max(1, r.laneCount);
      const acdx0 = cdx0 < 0 ? -cdx0 : cdx0;
      const inLaneRange = cdy < 6 && cdy > -1 && acdx0 < laneW * 0.55;
      const convergeChance = s.eLane[i] === playerLane
        ? r.convergeChance : r.crossLaneConvergeChance;
      const inCrossoverRange = !inLaneRange && cdy < CROSSOVER_RANGE_UNITS && cdy > 0
        && crossoverHash(i) < convergeChance;
      if (inLaneRange || inCrossoverRange) {
        const cdx = cdx0;
        const acdx = acdx0;
        if (acdx > 0.05) {
          let lunge: number;
          if (inCrossoverRange) {
            // GUARANTEED closure: close the whole remaining lateral gap
            // over the whole remaining approach time, so a converting
            // body is fully in the player's lane by the time it breaches.
            const ticksToBreach = Math.max(1, (cdy * TICK_RATE) / spd);
            lunge = acdx / ticksToBreach;
          } else {
            // A lunge, not a drift: closing speed rises as the gap shrinks.
            lunge = (spd * 0.9 * (1 - cdy / 6)) / TICK_RATE;
          }
          s.eX[i] += (cdx > 0 ? 1 : -1) * (lunge < acdx ? lunge : acdx);
        }
      }
    }

    // A boss NEVER breaches. It walks its lane to the squad's front line
    // and stops there, in the way, until it is killed. Previously a boss
    // that reached the squad was pool-freed for 4x damage and vanished,
    // which is why it could not be fought at all.
    if (def.isBoss) {
      // BOSS FIX (2026-08-26): stop distance now uses the SAME formula the
      // beast branch already does -- def.radius (itself derived from render
      // scale, packages/run-engine/src/constants.ts RADIUS_PER_SCALE), not
      // a separately authored magic number. The prior TRUE_BOSS_MELEE_REACH
      // constant (1.2) was picked to sit inside the render's old attack-pose
      // window and never accounted for how far the attack swing's own limb
      // travels past the body -- which is exactly what put the mesh's arm
      // across the squad with the contact test still reporting "not yet in
      // range". RADIUS_PER_SCALE was re-measured against that overlap
      // (see constants.ts) so this number now matches the drawn mesh.
      // BOSS HOLDS ITS LANE (founder finding, 2026-08-27): trackPlayerX
      // (see its own comment) was added to fix melee contact going stale
      // when the player drifted, but it reads wrong -- a boss this size
      // scooting sideways to stay lined up with wherever the squad's fire
      // is standing looks like the boss chasing the shot, not the shot
      // finding the boss. Reverted for the TRUE boss specifically: "boss
      // spawns, boss overrides every other lane decision" is already the
      // bot's own priority (see decide()/botTargetX's `if (boss >= 0)
      // return boss` -- unconditional, no dwell), so the player closes
      // this gap, not the boss. Left in place for the beast branch below,
      // which was never part of this report.
      const stop = s.playerY + def.radius + r.playerHitRadius - 0.05;
      if (s.eY[i] < stop) s.eY[i] = stop;
    } else if (s.eIsBoss[i] === 1) {
      // A beast holds the line instead: tough, blocking, survivable.
      const stop = s.playerY + def.radius + r.playerHitRadius - 0.05;
      if (s.eY[i] < stop) s.eY[i] = stop;
      trackPlayerX(s, i, spd);
    } else if (s.eY[i] < s.playerY - 1.2) {
      // BREACH. Anything that marches past the squad has got through, and
      // getting through costs you. Without this, straight-line movement
      // means a body in the other lane is simply ignorable and the safe
      // play is to sit in whichever lane is empty forever — the balance
      // sim found exactly that, timing out at 480s having killed 31
      // things. "Hold the corridor" has to mean something.
      //
      // Priced as a SHARE of the squad, not as a flat fraction of one
      // soldier. Flat, a leak cost 1.2hp: a 240-strong squad holds 2400,
      // so leaking every one of the 573 bodies the chapter authors cost
      // 688 -- dodging everything could not lose. Proportional, holding
      // the corridor means something at every squad size.
      const bodies = Math.max(r.breachMinBodies, s.squadSize * r.breachFractionOfSquad);
      blowX = s.eX[i]; blowY = s.eY[i];
      // SURVIVAL AUDIT: r.breachCostMult (1 everywhere it isn't overridden)
      // -- see its own comment. breachMinBodies assumes a squad has spares
      // to absorb a leak; a chapter locked at squad=1 does not.
      damageSquad(s, unitHp(s) * bodies * r.breachCostMult);
      emit(s, Ev.Breach, i, s.eDefIdx[i]);
      poolFree(s.ep, i);
      continue;
    }
    if (def.attackType === 'ranged') {
      if (s.eCooldown[i] > 0) s.eCooldown[i]--;
      else if (dist <= def.attackRange) {
        const qi = poolAlloc(s.qp);
        if (qi >= 0) {
          const sp = 8 / TICK_RATE;
          s.qX[qi] = s.eX[i]; s.qY[qi] = s.eY[i];
          s.qVx[qi] = (dx / dist) * sp; s.qVy[qi] = (dy / dist) * sp;
          s.qLife[qi] = 3 * TICK_RATE; s.qDamage[qi] = def.contactDamage;
        }
        s.eCooldown[i] = def.attackCooldownTicks;
      }
    }
  }

  // --- 9. weapon fire -------------------------------------------------------
  if (s.fireCooldown > 0) s.fireCooldown--;
  else {
    const w = cfg.weapons[s.weaponIdx]!;
    let n = volleySize(s.squadSize, s.statProjectiles, r.maxProjectilesPerVolley);
    if (n > r.maxProjectilesPerVolley) n = r.maxProjectilesPerVolley;
    if (n < 1) n = 1;
    // Total cone is CLAMPED, not per-projectile. A fixed per-projectile
    // offset compounds: thirty streams at six steps each is a 120-degree
    // sunburst that fires backwards. The cone stays tight and the streams
    // pack into it as the squad grows, which is what makes massed fire
    // read as a focused torrent.
    // Real volley fire is near-PARALLEL: each soldier shoots from their own
    // position toward the same target. Only a couple of degrees of scatter,
    // never a fan — a fan is what made it look like a firework instead of
    // a firing line. `w.pattern === 'spread_3'` gets exactly that: a BOUNDED
    // +/-1 dirTable step cone (1.41deg half-angle), the same absolute width
    // for every bolt regardless of volley size -- never the compounding
    // per-index offset the paragraph above describes. +/-2 steps (2.81deg)
    // was tried first and measured to spread 4.5u at 30u COMBINED with the
    // formation's existing 1.656u muzzle-origin spread (the two stack at
    // the extreme bolt) -- over the 3.6u lane. +/-1 measures 3.13u combined
    // at 30u, clear of the lane with margin, and needed no change to the
    // muzzle-origin spread itself (docs/DECISIONS.md, "Parallel fire
    // curtain" and its correction).
    const speed = w.projectileSpeed / TICK_RATE;
    // REACH-AXIS FIX (founder, 2026-08-20/21, see Rules.weaponRangeCeiling
    // and Rules.reachPerSquad): weapon_range_ceiling is now a sanity
    // ceiling (100 units) nothing authored reaches, not a working clamp.
    // weapons.csv's projectile_lifetime_sec authors a near-constant
    // per-tier BASE reach directly (~75 units, tier 1 through 12) --
    // damage, projectile count and fire rate carry tier progression
    // instead, so tier 1 reads as a working gun against the corridor
    // from the first shot. Squad size adds reachPerSquad units per
    // soldier past the first, keyed to s.squadSize directly rather than
    // to the volley's projectile count `n` -- the 90-projectile cap
    // already saturates `n` well before squad hits 20 for several
    // weapons (scattergun: 5x18=90), and keying reach to a saturated
    // count would flatten reach growth exactly where a growing squad
    // most wants it to keep climbing.
    const baseReachUnits = w.projectileSpeed * (w.projectileLifetimeTicks / TICK_RATE);
    // VISIBLE-KILL AUDIT: `weaponReachCap` (== weaponRangeCeiling, a no-op,
    // everywhere a chapter doesn't override it) is the binding constraint
    // isle 1/12 need -- base reach alone (~75u) already outranges their
    // 60u spawn depth at squad=1, so without this every enemy they ever
    // spawn could die before it was ever visible on screen.
    const effectiveReachUnits = Math.min(r.weaponReachCap,
      baseReachUnits + r.reachPerSquad * (s.squadSize - 1));
    const effectiveReachTicks = Math.max(1, Math.round((effectiveReachUnits / w.projectileSpeed) * TICK_RATE));

    // Fire STRAIGHT down the corridor — every stream parallel, none
    // angled, UNLESS w.pattern says otherwise. Streams originate ACROSS
    // the formation's width rather than all from one point, because each
    // soldier fires their own rifle forward. That is what makes massed
    // fire cover the lane instead of being a single hairline.
    const aim = 64; // +y in the generated unit table

    for (let i = 0; i < n; i++) {
      const pi = poolAlloc(s.pp);
      if (pi < 0) break;
      // 'forward': zero angular spread, same as always -- service_rifle
      // and every other forward-pattern weapon is unchanged.
      // 'spread_3': a BOUNDED +/-1 step offset (1.41deg half-angle),
      // derived from the allocated POOL INDEX rather than a new RNG
      // stream (no Stream enum entry, no stream-order obligation -- pool
      // allocation order is itself deterministic, same guarantee). Never
      // derived from `i`/n: that is exactly the per-index, volley-size-
      // compounding offset the comment above documents as the failure
      // mode this must not repeat.
      const d = w.pattern === 'spread_3'
        ? (aim + (pi % 3) - 1 + DIR_STEPS) % DIR_STEPS
        : aim;
      // Muzzles are spread across the FORMATION, not stacked on one point,
      // so a wide squad lays down a wide curtain of parallel fire.
      //
      // D12: `i/(n-1)-0.5` never lands on exactly 0 for an EVEN n --
      // symmetric evenly-spaced points around a centre can only include
      // that centre when the count is odd (n=2: ranks -0.5/+0.5, a gap
      // dead down the middle with nothing firing through it). A first
      // fix doubled the two middle soldiers onto rank 0 exactly -- wrong
      // the other way: two streams stacked on ONE line instead of a gap,
      // still not "every soldier fires its own line". Odd n is untouched
      // below (unchanged formula, unchanged output). Even n now treats
      // the formation as n EQUAL bins spanning the same +/-0.5 width and
      // fires from the CENTRE of each bin -- `(i+0.5)/n - 0.5` -- which
      // is strictly increasing in i (every soldier's rank is distinct,
      // no two ever coincide) and, because the centre of a bin is never
      // the shared edge between two bins, NEVER lands on exactly 0
      // either. The two innermost bins straddle the centre closely
      // (n=2: +/-0.25, a 0.5 gap, tighter than the old +/-0.5 spacing)
      // without a dead line down the middle or two streams on top of
      // each other.
      let rank: number;
      if (n <= 1) {
        rank = 0;
      } else if (n % 2 === 1) {
        rank = (i / (n - 1)) - 0.5;
      } else {
        rank = ((i + 0.5) / n) - 0.5;
      }
      const width = fireHalfWidth(r.arenaWidth, r.laneCount, s.squadSize);
      let ox = s.playerX + rank * width;
      if (ox < 0.3) ox = 0.3;
      if (ox > r.arenaWidth - 0.3) ox = r.arenaWidth - 0.3;
      s.pX[pi] = ox; s.pY[pi] = s.playerY + 0.35;
      s.pVx[pi] = cfg.dirTable[d * 2]! * speed;
      s.pVy[pi] = cfg.dirTable[d * 2 + 1]! * speed;
      s.pLife[pi] = effectiveReachTicks;
      s.pDamage[pi] = s.statDamage;
      s.pPierce[pi] = s.statPierce;
      // THE VEHICLE'S OWN GUNS: the last `extra` streams of the volley
      // (the flank muzzles) carry the vehicle's ×mult and a BOUNDED reach.
      // See recomputeStats: multiplying the whole curtain at full rifle
      // reach let one tank empty the street to the horizon.
      if (s.vehicleTicks > 0 && n - i <= Math.round(r.vehicleExtraStreams)) {
        s.pDamage[pi] = s.statDamage * r.vehicleDamageMult;
        const reachTicks = Math.max(1, Math.round((r.vehicleReach / w.projectileSpeed) * TICK_RATE));
        if (reachTicks < s.pLife[pi]) s.pLife[pi] = reachTicks;
      }
    }
    const rate = s.statFireRate <= 0 ? 1 : s.statFireRate;
    s.fireCooldown = Math.max(1, Math.round(w.cooldownTicks / rate));
  }

  // --- 10/11. projectiles, collision, deaths --------------------------------
  for (let i = 0; i < s.pp.cap; i++) {
    if (!s.pp.alive[i]) continue;
    // SWEPT movement. A fast round travels up to ~2.2 units per tick while
    // a zombie's whole hit window is ~0.5, so stepping the full velocity
    // and testing only the endpoint let bullets pass THROUGH bodies -- the
    // audit's "the cannon passes by some of the zombies". The tick is now
    // divided into substeps no longer than 0.3 units, colliding at each.
    const pvx = s.pVx[i], pvy = s.pVy[i];
    const spdT = Math.sqrt(pvx * pvx + pvy * pvy);
    const SUB = spdT > 0.3 ? Math.ceil(spdT / 0.3) : 1;
    let freed = false;
    for (let sub = 0; sub < SUB && !freed; sub++) {
    s.pX[i] += pvx / SUB; s.pY[i] += pvy / SUB;
    if ((sub === 0 && --s.pLife[i] <= 0) || s.pX[i] < -2 || s.pX[i] > r.arenaWidth + 2 ||
        s.pY[i] < s.playerY - 12 || s.pY[i] > s.playerY + r.spawnAhead + 4) {
      poolFree(s.pp, i); freed = true; break;
    }
    // Cell-major, pool index ascending — never Set/Map order.
    const cx = Math.floor(s.pX[i] / s.cellSize) + 1;
    const cy = Math.floor((s.pY[i] - s.playerY + 8) / s.cellSize) + 1;
    let hit = false;
    for (let oy = -1; oy <= 1 && !hit; oy++) {
      const ry = cy + oy;
      if (ry < 0 || ry >= s.gridRows) continue;
      for (let ox = -1; ox <= 1 && !hit; ox++) {
        const rx = cx + ox;
        if (rx < 0 || rx >= s.gridCols) continue;
        for (let e = s.cellHead[ry * s.gridCols + rx]; e !== -1; e = s.cellNext[e]) {
          if (!s.ep.alive[e]) continue;
          const def = cfg.enemies[s.eDefIdx[e]]!;
          // LANE-BLEED FIX: a lane-authored boss/beast's radius is sized
          // for its own silhouette, not for lane geometry -- see
          // lateralInRange's own comment. A shot fired from well across
          // the road must not connect just because the radius is wide
          // enough to reach.
          if (s.eIsBoss[e] === 1 && !lateralInRange(s.eX[e], s.pX[i], r)) continue;
          const ddx = s.eX[e] - s.pX[i];
          const ddy = s.eY[e] - s.pY[i];
          const rr = def.radius + 0.15;
          if (ddx * ddx + ddy * ddy > rr * rr) continue;

          s.eHp[e] -= s.pDamage[i];
          s.damageDealt += s.pDamage[i];
          // Direction is the projectile's heading -- the same vector the
          // knockback below uses, so the flinch, the sound and the freeze
          // all describe one impact instead of three guesses at it.
          // `tier` is the impact CLASS (titan vs body), never a raw damage
          // number: audio picks its layer and F3 sizes its freeze from it,
          // and neither may re-derive a design value. `lethal` is read
          // AFTER the subtraction, because the killing-blow reaction has to
          // be chosen at impact -- learning it from a later Death event is
          // a frame too late (C6 wants the body to grind before it dies).
          // `damage` rides alongside `tier` rather than instead of it. The
          // rule the contract enforces is that no consumer RE-DERIVES a
          // design value -- tier is handed over, never inferred from damage.
          // The raw number is still legitimately the renderer's to show.
          {
            const [hdx, hdy] = unitDir(s.pVx[i], s.pVy[i]);
            emit(s, Ev.Hit, e, hdx, hdy,
              s.eIsBoss[e] === 1 ? 2 : 0, s.eHp[e] <= 0 ? 1 : 0, s.pDamage[i]);
          }

          // Knockback impulse along the projectile's heading. The column
          // existed in weapons.csv and was ignored, so hits had no
          // physical push at all (AUDIT A-18). Bosses do not flinch.
          const kb = cfg.weapons[s.weaponIdx]!.knockback;
          if (kb > 0 && s.eIsBoss[e] !== 1) {
            const pv = Math.sqrt(s.pVx[i] * s.pVx[i] + s.pVy[i] * s.pVy[i]);
            if (pv > 0.0001) {
              s.eX[e] += (s.pVx[i] / pv) * kb;
              s.eY[e] += (s.pVy[i] / pv) * kb;
              // PHYSICS LAW: knockback ends at the rail, not through it.
              if (s.eX[e] < r.wallMargin) s.eX[e] = r.wallMargin;
              if (s.eX[e] > r.arenaWidth - r.wallMargin) s.eX[e] = r.arenaWidth - r.wallMargin;
            }
          }
          if (s.eHp[e] <= 0) {
            s.eHp[e] = 0;
            killEnemy(s, e, DeathCause.Blow, s.pVx[i], s.pVy[i]);
          } else {
            updateBossPhase(s, e);
          }

          // SPLASH. The aoe_radius column sat in the schema unwired, so a
          // packed rank could only be cleared file-by-file: N streams cover
          // N columns and the rest of a shoulder-to-shoulder rank walked
          // through untouched, no matter the damage numbers. Splash is what
          // makes a dense horde the thing fire is GOOD against -- one hit
          // fells a cluster -- which is the entire fantasy of the genre.
          const aoe = cfg.weapons[s.weaponIdx]!.aoeRadius;
          if (aoe > 0) {
            const splash = s.pDamage[i] * r.aoeSplashFraction;
            const rr2 = aoe * aoe;
            for (let e2 = 0; e2 < s.ep.cap; e2++) {
              if (!s.ep.alive[e2] || e2 === e) continue;
              const ax = s.eX[e2] - s.pX[i];
              const ay = s.eY[e2] - s.pY[i];
              if (ax * ax + ay * ay > rr2) continue;
              s.eHp[e2] -= splash;
              s.damageDealt += splash;
              if (s.eHp[e2] <= 0) {
                s.eHp[e2] = 0;
                killEnemy(s, e2, DeathCause.Blow);
              } else {
                updateBossPhase(s, e2);
              }
            }
          }
          if (--s.pPierce[i] < 0) { poolFree(s.pp, i); hit = true; freed = true; }
          break;
        }
      }
    }
    } // end substep sweep
  }

  // --- 12. enemy contact + enemy projectiles --------------------------------
  for (let i = 0; i < s.ep.cap; i++) {
    if (!s.ep.alive[i]) continue;
    const def = cfg.enemies[s.eDefIdx[i]]!;
    const dx = s.eX[i] - s.playerX;
    const dy = s.eY[i] - s.playerY;
    const rr = def.radius + r.playerHitRadius;
    // LANE-BLEED FIX: see lateralInRange's own comment -- a boss/beast
    // standing well across the road must not still be able to contact
    // the squad just because its radius is wide enough to reach. Fodder
    // is unaffected (its own radius never reaches a neighbouring lane).
    if (s.eIsBoss[i] === 1 && !lateralInRange(s.eX[i], s.playerX, r)) { /* out of range, no contact */ }
    else if (dx * dx + dy * dy <= rr * rr) {
      if (def.isBoss) {
        // A FIGHT, NOT A COIN FLIP (founder finding, 2026-08-25): contact
        // used to set `lostToBoss` and deal 9999 in the same tick it
        // reached the generic `rr` -- no longer flags an instant loss, it
        // is a repeating, cooldown-gated swing through the normal
        // `damageSquad` pipeline, same as a beast's, so a live shield still
        // soaks part of each hit and the squad can out-DPS it exactly like
        // every other threat in the run. BOSS FIX (2026-08-26): the melee
        // gate used to be a second, separately authored radius
        // (TRUE_BOSS_MELEE_REACH) instead of the same `rr` this very `if`
        // already gated on above -- always identical to the stop-clamp's
        // own radius now (def.radius, see that comment), so there is only
        // ever the one number. KILL-POWER AUDIT (2026-08-26): a swing pays
        // `unitHp(s)`, not the authored `def.contactDamage` -- a creature
        // this size connecting kills the one soldier it hit, same "ONE FOR
        // ONE" trade the ordinary fodder branch below already prices a
        // breach at, not a shared-pool chip number. Real physics: contact
        // is decisive, not a grind.
        //
        // FIGHT SHAPE AUDIT (founder finding, 2026-08-27): 1-per-swing has
        // no stable middle -- measured across a boss_hp_mult sweep, the
        // fight is either over before the boss ever lands a hit or it
        // survives long enough that the squad-shrinks-then-DPS-drops
        // spiral wipes the whole squad almost every time, with barely any
        // multiplier value landing between those two outcomes. `r.
        // bossSwingKillCount` (1 everywhere it isn't overridden, so every
        // OTHER chapter's math here is byte-identical to before) lets a
        // swing that connects be the dramatic, decisive hit it should be
        // without needing the boss to survive a long grind to matter.
        if (s.eCooldown[i] > 0) s.eCooldown[i]--;
        else {
          blowX = s.eX[i]; blowY = s.eY[i];
          damageSquad(s, unitHp(s) * r.bossSwingKillCount);
          s.eCooldown[i] = def.attackCooldownTicks;
        }
      } else if (s.eIsBoss[i] === 1) {
        // A stationary beast overlaps the squad for as long as it lives, so
        // damage has to be on a cooldown. KILL-POWER AUDIT: same one-
        // soldier-per-swing trade as the true boss above, not a flat
        // authored number -- see that branch's comment.
        if (s.eCooldown[i] > 0) s.eCooldown[i]--;
        else {
          blowX = s.eX[i]; blowY = s.eY[i];
          damageSquad(s, unitHp(s));
          s.eCooldown[i] = def.attackCooldownTicks;
        }
      } else {
        // ONE FOR ONE. A body that reaches the line takes a soldier with it
        // and dies doing it.
        //
        // This is the game's core exchange and it was never modelled. A
        // zombie dealt `contact_damage` (3) against a soldier's 180hp --
        // SIXTY of them to kill one man -- so the horde was a slow tax
        // rather than an enemy, and a line that holds or breaks never
        // existed. The thing the player actually watches, a zombie reaching
        // a soldier, had no mechanical meaning at all.
        //
        // The visual IS the mechanic now. Density is unchanged: the street
        // stays full and the column still runs to the horizon. What changes
        // is that the curtain of fire has to STOP that column, because
        // every body it fails to stop costs exactly one soldier.
        blowX = s.eX[i]; blowY = s.eY[i];
        damageSquad(s, unitHp(s));
        killEnemy(s, i, DeathCause.Breach);
      }
    }
  }
  for (let i = 0; i < s.qp.cap; i++) {
    if (!s.qp.alive[i]) continue;
    s.qX[i] += s.qVx[i]; s.qY[i] += s.qVy[i];
    if (--s.qLife[i] <= 0) { poolFree(s.qp, i); continue; }
    const dx = s.qX[i] - s.playerX;
    const dy = s.qY[i] - s.playerY;
    if (dx * dx + dy * dy <= r.playerHitRadius * r.playerHitRadius) {
      blowX = s.qX[i]; blowY = s.qY[i];
      damageSquad(s, s.qDamage[i]);
      poolFree(s.qp, i);
    }
  }

  // --- 13. pickups ----------------------------------------------------------
  for (let i = 0; i < s.kp.cap; i++) {
    if (!s.kp.alive[i]) continue;
    const dx = s.playerX - s.kX[i];
    const dy = s.playerY - s.kY[i];
    const distSq = dx * dx + dy * dy;
    const rad = s.statPickupRadius;
    // Orbs ALWAYS travel to the squad — no range gate. A ranged magnet
    // strands every orb dropped beyond it, which silently deleted the
    // entire XP economy: forty kills would leave forty orbs lying in the
    // corridor and the run stuck at level 1. Distance only sets how fast
    // they come, so positioning still pays.
    if (distSq > rad * rad) {
      const dist = Math.sqrt(distSq);
      const drift = (r.magnetSpeed * 0.55) / TICK_RATE;
      s.kX[i] += (dx / dist) * drift;
      s.kY[i] += (dy / dist) * drift;
    }
    if (distSq <= rad * rad) {
      const dist = Math.sqrt(distSq);
      if (dist <= 0.45) {
        if (s.kKind[i] === PICKUP_XP) s.xp += s.kValue[i];
        else if (s.kKind[i] === PICKUP_RECRUIT) {
          gainSquad(s, s.squadSize + s.kValue[i]);
        } else {
          // A field pickup can only PROMOTE. Letting it assign the rolled
          // index outright meant a random drop could silently undo the
          // armory gate you just chose over a squad bonus.
          if (s.kValue[i] > s.weaponIdx) { s.weaponIdx = s.kValue[i]; recomputeStats(s); }
        }
        emit(s, Ev.PickedUp, -1, s.kKind[i], s.kValue[i]);
        poolFree(s.kp, i);
        continue;
      }
      const step = r.magnetSpeed / TICK_RATE;
      s.kX[i] += (dx / dist) * step;
      s.kY[i] += (dy / dist) * step;
    }
  }

  // --- 13a2. projectiles chip the monolith ---------------------------------
  // THE DILEMMA, in one loop. A round that strikes the rock is CONSUMED --
  // it does not carry on into the horde behind it, no matter how much
  // pierce it has. Every point of damage on that counter is a body that
  // stayed alive in the other lane, and the player pays that price
  // continuously rather than at a gate.
  for (let mi = 0; mi < s.mp.cap; mi++) {
    if (!s.mp.alive[mi] || s.mHp[mi] <= 0) continue;
    const mx = s.mX[mi], my = s.mY[mi];
    // ISLE-12 REBUILD, CORRECTED (founder finding, 2026-08-25): a race
    // monolith stands in one lane, same footprint as every other
    // monolith -- the road-width hit box (half the arena width) matched
    // the road-width PLACEMENT this same session's earlier version used,
    // and both are reverted together. See spawnMonolith's mX assignment.
    const halfW = MONOLITH_HALF_W;
    for (let p = 0; p < s.pp.cap; p++) {
      if (!s.pp.alive[p]) continue;
      if (Math.abs(s.pY[p] - my) > MONOLITH_HALF_D) continue;
      if (Math.abs(s.pX[p] - mx) > halfW) continue;
      s.mHp[mi] -= s.pDamage[p];
      s.damageDealt += s.pDamage[p];
      poolFree(s.pp, p);
      if (s.mHp[mi] <= 0) {
        s.mHp[mi] = 0;
        breakBlock(s, mi);
        poolFree(s.mp, mi);
        break;
      }
    }
  }

  // --- 13b. projectiles chew through barriers -------------------------------
  for (let i = 0; i < s.gp.cap; i++) {
    if (!s.gp.alive[i]) continue;
    const aop = s.gOp[i];
    // Crates are shot open the same way barriers are. Filtering on
    // GATE_BARRIER alone left every crate invulnerable -- its armour
    // counted down on screen but nothing could ever reduce it.
    const armoured = aop === GATE_BARRIER || aop === GATE_CRATE || aop === GATE_VEHICLE
      || aop === GATE_ALLY || aop === GATE_ARMOUR;
    // THE DIAL (founder, 2026-08-22): add/subtract/multiply lanes are shot
    // the same way, but a hit PUMPS gValue instead of dealing armour
    // damage -- gates.csv's pump_per_hit is 0 for every chapter but 12
    // (spawned into s.gPump, section 14), so this branch is dead there:
    // exactly the behaviour before this lane type existed.
    const pumpable = (aop === GATE_ADD || aop === GATE_SUB || aop === GATE_MUL) && s.gPump[i] > 0;
    if (armoured && s.gHp[i] <= 0) continue;
    if (!armoured && !pumpable) continue;
    const lanes = cfg.gateGroups[s.gGroup[i]]!.lanes.length;
    const gx = ((s.gLane[i] + 0.5) / lanes) * r.arenaWidth;
    const halfW = r.arenaWidth / lanes / 2;
    for (let p = 0; p < s.pp.cap; p++) {
      if (!s.pp.alive[p]) continue;
      if (Math.abs(s.pY[p] - s.gY[i]) > 0.45) continue;
      if (Math.abs(s.pX[p] - gx) > halfW) continue;
      if (armoured) {
        s.gHp[i] -= s.pDamage[p];
        s.damageDealt += s.pDamage[p];
        poolFree(s.pp, p);
        if (s.gHp[i] <= 0) {
          s.gHp[i] = 0;
          emit(s, Ev.GateBreak, i, s.gOp[i], s.gValue[i]);
          break;
        }
      } else {
        // Consumed the same way a barrier hit is -- it must not ALSO
        // reach the enemies behind the gate.
        s.gValue[i] += s.gPump[i];
        poolFree(s.pp, p);
      }
    }
  }

  // --- 14. gates ------------------------------------------------------------
  // ISLE-12 PRE-PLACEMENT (2026-08-22, isle 12 only, by explicit task
  // scope -- not generalised to every chapter). The loop below spawns
  // each group at `s.playerY + gateSpawnAhead`, computed FRESH at that
  // group's own spawn tick: since resolve-travel time (gateSpawnAhead /
  // (scrollSpeed+gateApproachSpeed), 5s at this chapter's rates) is
  // shorter than the gap between successive groups' `at_sec` in every
  // authored chapter, only one group is ever in flight and the road ahead
  // is empty -- nothing to plan a route against. Chapter 12's groups
  // instead spawn ALL AT ONCE at run start (tick 1), each placed at the
  // absolute depth its own `at_sec` implies, then travel toward the
  // player on the exact same per-tick rails the loop below already uses --
  // pre-placement only changes WHEN and WHERE a group is born, never how
  // it moves or resolves once alive.
  //
  // gY0 = playerY + at_sec*(scrollSpeed+gateApproachSpeed) + gateSpawnAhead.
  // NOTE on the formula: naively using gY = at_sec*rate (treating at_sec
  // as the depth-at-time-zero) makes a group ARRIVE at the player at
  // t=at_sec -- i.e. it treats at_sec as RESOLVE time. gates.csv documents
  // at_sec as SPAWN time, and the loop below's own resolve time is
  // at_sec + gateSpawnAhead/rate (algebra: gY(t) = playerY+gateSpawnAhead
  // - rate*(t-at_sec), solved for gY=playerY). Dropping the +gateSpawnAhead
  // term would resolve every group ~5s EARLIER than before, which is
  // exactly what this task's own acceptance bar ("resolves within 0.2s of
  // its previous resolve time") would catch. Adding +playerY and
  // +gateSpawnAhead here is that same algebra solved backward for gY0
  // instead of for t_resolve, so it reproduces the old resolve tick
  // exactly regardless of playerY's actual value (playerY cancels out of
  // t_resolve either way) -- verified against the pre-fix measured
  // resolve ticks, see the commit body.
  // REWARD "STAIRCASE" PREVIEW -- ATTEMPTED AND REVERTED (2026-09-05):
  // generalising isle 12's gate-pre-placement mechanism to isle 1 hung
  // the run-engine test suite (two vitest workers spinning at ~55% CPU
  // for 7+ minutes with zero output -- a real pathological loop, not
  // slow property tests), killed before finding the exact cause. Reverted
  // rather than debugged under time pressure with the founder waiting on
  // higher-priority fixes (slowness, a reverted render feature, contact
  // VFX). Root cause NOT YET FOUND -- do not re-attempt this by just
  // flipping the condition back without first finding why it hangs.
  if (s.chapter === 12 && s.tick === 1) {
    for (let gidx = 0; gidx < cfg.gateGroups.length; gidx++) {
      const preGroup = cfg.gateGroups[gidx]!;
      const atSec = preGroup.atTick / TICK_RATE;
      const gY0 = s.playerY + atSec * (r.scrollSpeed + r.gateApproachSpeed) + r.gateSpawnAhead;
      for (const lane of preGroup.lanes) {
        const gi = poolAlloc(s.gp);
        if (gi < 0) break;
        s.gY[gi] = gY0;
        s.gValue[gi] = lane.value;
        s.gHp[gi] = lane.armor;
        s.gPump[gi] = lane.pumpPerHit;
        s.gOp[gi] = GATE_OP_CODE[lane.op];
        s.gLane[gi] = lane.laneIndex;
        s.gGroup[gi] = gidx;
      }
    }
    // Every group is already placed -- the loop below must never spawn
    // any of them again when their own at_sec/atTick naturally arrives.
    s.gateCursor = cfg.gateGroups.length;
  }
  while (s.gateCursor < cfg.gateGroups.length && cfg.gateGroups[s.gateCursor]!.atTick <= s.tick) {
    const group = cfg.gateGroups[s.gateCursor]!;
    for (const lane of group.lanes) {
      const gi = poolAlloc(s.gp);
      if (gi < 0) break;
      s.gY[gi] = s.playerY + r.gateSpawnAhead;
      s.gValue[gi] = lane.value;
      s.gHp[gi] = lane.armor;
      s.gPump[gi] = lane.pumpPerHit;
      s.gOp[gi] = GATE_OP_CODE[lane.op];
      s.gLane[gi] = lane.laneIndex;
      s.gGroup[gi] = s.gateCursor;
    }
    s.gateCursor++;
  }
  for (let i = 0; i < s.gp.cap; i++) {
    if (!s.gp.alive[i]) continue;
    // Gates ride the corridor PLUS an authored approach speed (per-chapter
    // gate_speed). At scroll-only closing (3 u/s) a gate spends 6.7s of
    // its 10s transit inside the commit window, so any beat pitched under
    // ~14s overlaps its neighbour BY CONSTRUCTION — measured twice on the
    // beat grammar (19% and 22% clean windows at 9-14s pitch). The
    // reference games close their gates in 3-4 visible seconds; this is
    // that, as config.
    s.gY[i] -= (r.scrollSpeed + r.gateApproachSpeed) / TICK_RATE;
    if (s.gY[i] > s.playerY) continue;
    // This lane's group has reached the player. Resolve by occupied lane.
    const groupIdx = s.gGroup[i];
    const laneCount = cfg.gateGroups[groupIdx]!.lanes.length;
    const occupied = Math.min(laneCount - 1, Math.floor((s.playerX / r.arenaWidth) * laneCount));
    for (let j = 0; j < s.gp.cap; j++) {
      if (!s.gp.alive[j] || s.gGroup[j] !== groupIdx) continue;
      if (s.gLane[j] === occupied) {
        const before = s.squadSize;
        // Ramming an armoured gate costs a SHARE of the squad scaled by how
        // intact the armour still is: untouched = half the squad, nearly
        // broken = a scratch. Armour is authored in damage points since
        // v3.1, so the old flat `gHp * 0.5` would read thousands of hp and
        // one-shot any squad that touched an unbroken wall.
        const ldef = cfg.gateGroups[groupIdx]!.lanes
          .find((l) => l.laneIndex === s.gLane[j]);
        // Ramming an intact wall costs at most a third of the squad, not
        // half. The real price of passing a crate is the gun you did not
        // get; making the collision itself gut you as well meant a wall
        // thick enough to be a decision was also a wall that ended runs.
        const ramCost = ldef && ldef.armor > 0
          ? s.squadHpPool * 0.33 * (s.gHp[j] / ldef.armor) : 0;
        // HOW FULL DID IT ARRIVE. This is the station's whole economy: the
        // reward is graded by the share of your firepower you spent on it
        // rather than on the lane the horde is using. Below Fizzle it
        // sparks out and pays nothing at all -- a station you only glanced
        // at is a station you did not buy.
        const charge = ldef && ldef.armor > 0
          ? 1 - Math.max(0, s.gHp[j]) / ldef.armor : 1;
        const tier = charge >= CHARGE_FULL ? ChargeTier.Full
          : charge >= CHARGE_PARTIAL ? ChargeTier.Partial : ChargeTier.Fizzle;
        if (s.gOp[j] === GATE_ARMORY) {
          // Armory lane: no bodies gained, the ARMS improve. This is the
          // other half of the binary choice — grow wide, or grow deadly.
          const next = drawWeapon(s, Stream.UpgradeOffers);
          if (next > s.weaponIdx) { s.weaponIdx = next; recomputeStats(s); }
          emit(s, Ev.Armory, -1, s.weaponIdx, 0);
        } else if (s.gOp[j] === GATE_VEHICLE) {
          // A PARTIAL charge still delivers the tank -- for a fraction of
          // its running time. Half a job pays half a prize; that is what
          // makes the last quarter of a charge worth fighting for instead
          // of a threshold you either clear or ignore.
          if (tier !== ChargeTier.Fizzle) {
            const frac = tier === ChargeTier.Full ? 1 : 0.45;
            s.vehicleTicks = Math.round(r.vehicleDurationSec * TICK_RATE * frac);
            recomputeStats(s);
            emit(s, Ev.Armory, -1, s.weaponIdx, 2);
          } else damageSquad(s, ramCost, false);
          emit(s, Ev.Charged, -1, tier, s.gOp[j]);
        } else if (s.gOp[j] === GATE_CRATE) {
          // A gun cannot arrive at 45% strength, so a partial charge buys
          // the NEXT rung and a full charge can jump two -- the ladder
          // itself is the gradient.
          if (tier !== ChargeTier.Fizzle) {
            const next = drawWeapon(s, Stream.UpgradeOffers);
            if (next > s.weaponIdx) { s.weaponIdx = next; recomputeStats(s); }
            if (tier === ChargeTier.Full) {
              const cap = Math.min(cfg.weaponCap, cfg.weapons.length - 1);
              if (s.weaponIdx < cap) { s.weaponIdx++; recomputeStats(s); }
            }
            emit(s, Ev.Armory, -1, s.weaponIdx, 1);
          } else damageSquad(s, ramCost, false);
          emit(s, Ev.Charged, -1, tier, s.gOp[j]);
        } else if (s.gOp[j] === GATE_ALLY) {
          // THE MONSTER YOU PAID FOR. It is not a stat: it walks out of the
          // station into the horde and fights until it dies.
          if (tier !== ChargeTier.Fizzle) {
            spawnAlly(s, s.gLane[j], tier === ChargeTier.Full ? 1 : 0.5);
          } else damageSquad(s, ramCost, false);
          emit(s, Ev.Charged, -1, tier, s.gOp[j]);
        } else if (s.gOp[j] === GATE_ARMOUR) {
          if (tier !== ChargeTier.Fizzle) {
            // Armour is priced in SOLDIERS so it reads against the only
            // number the player tracks: a full charge is worth `value`
            // bodies of punishment that cost no bodies.
            const add = unitHp(s) * s.gValue[j] * (tier === ChargeTier.Full ? 1 : 0.45);
            s.shieldHp += add;
            if (s.shieldHp > s.shieldMax) s.shieldMax = s.shieldHp;
            // REFRESH THE POOL ON A SUCCESSFUL CHARGE. Every other
            // resolution (add/subtract/multiply/barrier/armscrate/vehicle/
            // ally) routes through `gainSquad`, which recomputes
            // `squadHpPool` from the resulting squadSize even when the
            // size doesn't change -- armour was the one path that didn't,
            // so a player who correctly reads every gate as the safe lane
            // (never touching an add/subtract/multiply lane, where the
            // reset already happens) had their pool silently drained by
            // ordinary background breach damage from enemies in lanes they
            // were never in and had no reason to engage, with nothing
            // ever topping it back up. Found isle-12 rebuild session
            // 2026-08-24: a "steer to the cheaper lane every time" policy
            // died mid-corridor from accumulated 9-point breach chip, not
            // from any gate or any enemy it ever actually faced. Fizzle
            // stays untouched -- damageSquad's ramCost is the intended
            // cost of not charging it.
            gainSquad(s, s.squadSize);
          } else damageSquad(s, ramCost, false);
          emit(s, Ev.Charged, -1, tier, s.gOp[j]);
        } else if (s.gOp[j] === GATE_BARRIER) {
          // Barrier lane: the number was its HP. Shoot it down before you
          // reach it and the payout is large; ram it intact and it costs.
          if (s.gHp[j] <= 0) {
            gainSquad(s, before + Math.round(s.gValue[j] / r.barrierPayoutDiv));
            emit(s, Ev.BarrierDown, -1, Math.round(s.gValue[j]), s.squadSize - before);
          } else {
            damageSquad(s, ramCost, false);
            emit(s, Ev.GatePassed, -1, s.gOp[j], s.squadSize - before);
          }
        } else {
          gainSquad(s, applyGateOp(before, s.gOp[j], s.gValue[j], Infinity));
          emit(s, Ev.GatePassed, -1, s.gOp[j], s.squadSize - before);
        }
      }
      poolFree(s.gp, j);
    }
  }

  // --- 15. level-up ---------------------------------------------------------
  while (s.xp >= s.xpToNext && s.level < r.maxLevel) {
    s.xp -= s.xpToNext;
    s.level++;
    s.pendingLevelUps++;
    s.xpToNext = cfg.xpTable[Math.min(s.level - 1, cfg.xpTable.length - 1)]!;
    emit(s, Ev.LevelUp, -1, s.level, 0);
  }
  // A-45: the run still NEVER pauses (the founder's own "vibe killer"
  // ruling on a modal stands) -- but a level-up now opens a short,
  // non-blocking window during which a `levelupChoice` command picks the
  // upgrade instead of the seeded random draw. Unattended (no command
  // before the window closes, or an out-of-range index), it falls back to
  // the EXACT pre-A-45 draw (`nextInt` over the same offered set), so an
  // old replay with no player input is byte-identical to before this
  // feature existed. One window open at a time; a level-up spree queues
  // through `pendingLevelUps` the same way it always has.
  if (s.boonWindowTicks > 0) {
    s.boonWindowTicks--;
    const cmd = input.command;
    const picked = cmd?.k === 'levelupChoice' && cmd.index >= 0 && cmd.index < s.offerCount;
    if (picked || s.boonWindowTicks <= 0) {
      const pick = picked
        ? s.offerOptions[cmd!.index]!
        : s.offerOptions[nextInt(s.rng, Stream.UpgradeOffers, s.offerCount)]!;
      s.upgradeStacks[pick]++;
      recomputeStats(s);
      emit(s, Ev.Boon, -1, pick, s.level);
      s.boonWindowTicks = 0;
      s.offerCount = 0;
    }
  }
  if (s.boonWindowTicks <= 0 && s.pendingLevelUps > 0) {
    drawOffers(s);
    if (s.offerCount === 0) { s.pendingLevelUps = 0; }
    else { s.pendingLevelUps--; s.boonWindowTicks = Math.round(r.boonWindowSec * TICK_RATE); }
  }

  // --- 15b. squad formation ---------------------------------------------
  // Ported from scene3d.ts's render() (Contact and Impact, "squad bodies"
  // part 1) -- a pure function of squadSize/playerX/playerY plus fixed
  // constants, no randomness, no per-frame render state, per that report's
  // own finding. Placed after every section that can still move squadSize
  // this tick (allies, contact kills, pickups, gates, level-up) and BEFORE
  // end conditions, which return early on several branches -- computing
  // formation after those would skip it on the exact tick a run ends.
  //
  // SIM-SPACE now (Contact and Impact, "squad bodies" part 2) -- part 1
  // stored these in world-space render coordinates and said plainly that a
  // pass wanting sim-space for collision was free to add it; this is that
  // pass. Every OTHER pooled entity (eX/eY, pX/pY, kX/kY, gLane...) is
  // already sim-space, with the renderer converting to world-space at draw
  // time via `(simX - arenaW/2) * SX, simY * S` (see scene3d.ts:5556 for
  // enemies) -- sX/sY now follow the same convention, which is what makes
  // reusing the enemy grid/collision math below possible at all. The
  // reparameterisation below is exact: only X-axis LENGTHS (deckHalf,
  // fileStep, and everything derived from them) are divided by FORM_SX to
  // move them into sim-space, because FORM_SX is the only axis scale
  // between the two spaces (FORM_S = 1 on Y/Z, i.e. playerY/eY were
  // already the same units as the old world-space Z). Expanding the
  // renderer's `(simX - arenaW/2) * SX` against this section's simCx
  // algebraically reproduces the old worldCx bit-for-bit -- verified by
  // screenshot/position diff at squad sizes 1, 5 and 16 (see commit
  // message), all axes 0 delta.
  {
    const prevCount = s.sp.count; // last tick's live count, read before we overwrite it
    const count = Math.max(0, Math.min(s.squadSize, s.sp.cap));
    for (let i = 0; i < s.sp.cap; i++) s.sp.alive[i] = i < count ? 1 : 0;
    s.sp.count = count;

    // shapeInto(drawn, outX, outY): this tick's (or a hypothetical count's)
    // canonical sim-space slot positions, written into caller-owned scratch
    // buffers. A closure so death-in-place below can also evaluate the
    // PRE-damage shape without duplicating the formula. Was an
    // allocate-and-return-{x,y}[] closure (AUDIT A-13): at squadSize
    // 16-20 that was 1000+ small object allocations/sec for the run's
    // whole duration, on a tick this section runs unconditionally every
    // time. Writing into pre-allocated `s.shapeScratch*` (state.ts) instead
    // produces the SAME numeric values in the SAME order -- purely a
    // storage change, not a formula change.
    const shapeInto = (drawn: number, outX: Float64Array, outY: Float64Array): void => {
      if (drawn <= 0) return;
      // scene3d.ts:4789-4791. MAX_DRAWN (260) never binds here: squadSize
      // is already clamped well under it by rules.squad_max elsewhere.
      const cols = Math.max(3, Math.min(16, Math.ceil(Math.sqrt(drawn * 1.6))));
      // scene3d.ts:4798, sim-space: old world deckHalf / FORM_SX.
      const deckHalf = r.arenaWidth / 2 - 0.9 / FORM_SX;
      // scene3d.ts:4815-4817
      const crowdScale = drawn <= 8 ? 1 : 1 - 0.22 * Math.min(1, (drawn - 8) / 12);
      // sim-space: old world fileStep / FORM_SX (X-axis length only).
      const fileStep = (FORM_FILE_GAP * FORM_SOLDIER_SCALE * FORM_SQUAD_MULT
        * crowdScale * FORM_RIG_FIT * FORM_SOLDIER_BULK * FORM_S) / FORM_SX;
      // rankStep needs no conversion -- Y/Z carries no FORM_SX scaling.
      const rankStep = FORM_RANK_GAP * FORM_SOLDIER_SCALE * FORM_SQUAD_MULT
        * crowdScale * FORM_RIG_FIT * FORM_S;
      // scene3d.ts:4818-4822. Squeeze is a dimensionless ratio (naturalHalf
      // / deckHalf) so it comes out identical in either unit system.
      const naturalHalf = ((cols - 1) / 2) * fileStep;
      const squeeze = naturalHalf > deckHalf * 0.62 ? (deckHalf * 0.62) / naturalHalf : 1;
      const formHalf = naturalHalf * squeeze;
      // sim-space anchor: playerX directly (no arenaWidth/2 offset, no SX
      // scale -- those only existed to convert INTO world-space).
      const cx = Math.max(r.arenaWidth / 2 - deckHalf + formHalf,
        Math.min(r.arenaWidth / 2 + deckHalf - formHalf, s.playerX));
      // scene3d.ts:4833-4834
      const totalRows = Math.ceil(drawn / cols);
      const zAnchor = s.playerY + ((totalRows - 1) * rankStep) * 0.5;
      for (let i = 0; i < drawn; i++) {
        const row = Math.floor(i / cols);
        const inRow = i % cols;
        const inThisRow = Math.min(cols, drawn - row * cols);
        outX[i] = cx + (inRow - (inThisRow - 1) / 2) * fileStep * squeeze;
        outY[i] = zAnchor - row * rankStep;
      }
    };

    shapeInto(count, s.shapeScratchX, s.shapeScratchY);
    for (let i = 0; i < count; i++) { s.sX[i] = s.shapeScratchX[i]!; s.sY[i] = s.shapeScratchY[i]!; }

    // DEATH IN PLACE. The renderer's own death-hold (scene3d.ts, "Slot
    // just lost: start the death performance and hold the body") already
    // freezes a soldier at wherever it last stood once its pool index
    // falls outside the live count -- so "in place" is really a question
    // of WHICH index falls out. Today that is always the highest-numbered
    // one, regardless of where it actually stood. Fix: on a tick that
    // actually lost bodies to a genuine blow (not a toll -- see blowX/
    // blowY's own doc comment at the top of this function), take the
    // OLD (pre-damage) formation's positions, find the ones nearest the
    // blow, and write those into the index range that is about to become
    // "dead" (count..prevCount) -- overwriting stale leftover values with
    // the one meaningful position a corpse should hold. The renderer reads
    // this exact index one more time, on the frame the death performance
    // starts, before it stops updating (see scene3d.ts's death-trigger
    // branch), so this is the one write that is not wasted.
    const lost = prevCount - count;
    if (lost > 0 && Number.isFinite(blowX) && prevCount > 0) {
      const oldCount = Math.min(prevCount, s.sp.cap);
      shapeInto(oldCount, s.shapeScratchOldX, s.shapeScratchOldY);
      const bx = blowX, by = blowY;
      // The `lost` old-formation slots NEAREST the blow, closest first --
      // was `.map().sort().slice(0, lost)` (AUDIT A-13, an allocation on
      // every tick a hit costs a body). Equivalent without allocating:
      // repeatedly take the closest remaining slot. Same values, same
      // order (each pass finds the next-smallest distance, which is what
      // a full ascending sort's first `lost` entries are), zero heap.
      const used = s.shapeScratchUsed;
      for (let i = 0; i < oldCount; i++) used[i] = 0;
      for (let k = 0; k < lost; k++) {
        let bestI = -1, bestD2 = Infinity;
        for (let i = 0; i < oldCount; i++) {
          if (used[i]) continue;
          const dx = s.shapeScratchOldX[i]! - bx, dy = s.shapeScratchOldY[i]! - by;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) { bestD2 = d2; bestI = i; }
        }
        if (bestI < 0) break;
        used[bestI] = 1;
        const deadI = count + k;
        if (deadI >= s.sp.cap) break;
        s.sX[deadI] = s.shapeScratchOldX[bestI]!;
        s.sY[deadI] = s.shapeScratchOldY[bestI]!;
      }
    }
  }

  // --- 15c. squad & enemy separation --------------------------------------
  // Contact and Impact, "squad bodies" part 2. Nothing collided with a
  // soldier body before this tick -- enemies walked through the formation
  // and soldiers overlapped each other. Reuses the SAME spatial grid
  // (rebuildGrid, section 7) and the SAME 3x3-neighbourhood walk the
  // projectile collision pass above already uses (section 10/11), built
  // from enemy positions at the START of the tick -- one tick of staleness
  // against this section's later knockback-adjusted positions, same as the
  // projectile pass already tolerates.
  {
    const count = s.sp.count;
    // scene3d.ts:5263's existing `contact()` patch radius (0.34) is a
    // ground-decal constant, not a collision radius -- it is never read by
    // physics anywhere, so it is not reused here. This radius instead comes
    // from the soldier rig's own bind-pose Box3 (FORM_RIG_DEPTH_NATIVE,
    // see its doc comment above), scaled by the SAME crowdScale the
    // formation above just used for this tick's count, halved for a
    // centre-to-edge radius. Shrinks with crowdScale exactly as the
    // rendered body does, so a dense squad's tighter spacing and smaller
    // bodies never fight each other.
    const crowdScale = count <= 8 ? 1 : 1 - 0.22 * Math.min(1, (count - 8) / 12);
    const soldierRadius = (FORM_RIG_DEPTH_NATIVE * FORM_RIG_FIT * FORM_SOLDIER_SCALE
      * FORM_SQUAD_MULT * FORM_SOLDIER_BULK * crowdScale) / 2;

    // ITERATE. A single Gauss-Seidel sweep leaves small residuals on any
    // overlap chain longer than two bodies (A pushed clear of B lands back
    // on C) -- measured directly: one pass left the tightest pair 2.4%
    // short of soldierRadius*2 at chapter 12 tick 1500. A fixed handful of
    // sweeps closes that without a real solver; SEPARATION_ITERATIONS
    // sweeps of an O(n^2 + grid) pass, n<=20 for soldiers and the existing
    // enemy pool for the grid walks, measured well inside the frame budget
    // (see commit message).
    const minSep = soldierRadius * 2 * SEPARATION_MARGIN;
    for (let iter = 0; iter < SEPARATION_ITERATIONS; iter++) {
      // SOLDIER vs SOLDIER. count <= squad_max (20): O(n^2) is <=190 pair
      // checks, cheaper than building a grid for a pool this small.
      for (let a = 0; a < count; a++) {
        for (let b = a + 1; b < count; b++) {
          const dx = s.sX[b] - s.sX[a], dy = s.sY[b] - s.sY[a];
          const d2 = dx * dx + dy * dy;
          if (d2 >= minSep * minSep) continue;
          let nx: number, ny: number, d: number;
          if (d2 < 1e-9) {
            // Exact overlap: no direction to push along. A fixed,
            // deterministic nudge (never RNG, per Iron Rule 1) breaks the
            // tie the same way every replay.
            nx = 1; ny = 0; d = 0;
          } else {
            d = Math.sqrt(d2); nx = dx / d; ny = dy / d;
          }
          const push = (minSep - d) / 2;
          s.sX[a] -= nx * push; s.sY[a] -= ny * push;
          s.sX[b] += nx * push; s.sY[b] += ny * push;
        }
      }

      // SOLDIER vs ENEMY. Soldiers hold the formation they were just placed
      // in; enemies get shoved out of a soldier's body, matching the
      // FINDING's own framing ("push enemies out of soldiers").
      for (let i = 0; i < count; i++) {
        const sxp = s.sX[i], syp = s.sY[i];
        const cx = Math.floor(sxp / s.cellSize) + 1;
        const cy = Math.floor((syp - s.playerY + 8) / s.cellSize) + 1;
        for (let oy = -1; oy <= 1; oy++) {
          const ry = cy + oy;
          if (ry < 0 || ry >= s.gridRows) continue;
          for (let ox = -1; ox <= 1; ox++) {
            const rx = cx + ox;
            if (rx < 0 || rx >= s.gridCols) continue;
            for (let e = s.cellHead[ry * s.gridCols + rx]; e !== -1; e = s.cellNext[e]) {
              if (!s.ep.alive[e]) continue;
              const def = cfg.enemies[s.eDefIdx[e]]!;
              const dx = s.eX[e] - sxp, dy = s.eY[e] - syp;
              // PACKING radius, not combat radius -- see PACKING_RADIUS_FRACTION's
              // own comment. soldierRadius is untouched (squad packing is
              // out of scope for this fix).
              const rr = (def.radius * PACKING_RADIUS_FRACTION + soldierRadius) * SEPARATION_MARGIN;
              const d2 = dx * dx + dy * dy;
              if (d2 >= rr * rr) continue;
              let nx: number, ny: number, d: number;
              if (d2 < 1e-9) { nx = 0; ny = 1; d = 0; } else { d = Math.sqrt(d2); nx = dx / d; ny = dy / d; }
              const push = rr - d;
              s.eX[e] += nx * push; s.eY[e] += ny * push;
              // PHYSICS LAW: pushed clear of a soldier, never through a rail.
              if (s.eX[e] < r.wallMargin) s.eX[e] = r.wallMargin;
              if (s.eX[e] > r.arenaWidth - r.wallMargin) s.eX[e] = r.arenaWidth - r.wallMargin;
            }
          }
        }
      }

      // ENEMY vs ENEMY. "Free once the grid walk exists" (part 2 report).
      // Mutual push, split evenly; `e <= i` skips a pair once it has
      // already been resolved from the other side.
      for (let i = 0; i < s.ep.cap; i++) {
        if (!s.ep.alive[i]) continue;
        const defI = cfg.enemies[s.eDefIdx[i]]!;
        const cx = Math.floor(s.eX[i] / s.cellSize) + 1;
        const cy = Math.floor((s.eY[i] - s.playerY + 8) / s.cellSize) + 1;
        for (let oy = -1; oy <= 1; oy++) {
          const ry = cy + oy;
          if (ry < 0 || ry >= s.gridRows) continue;
          for (let ox = -1; ox <= 1; ox++) {
            const rx = cx + ox;
            if (rx < 0 || rx >= s.gridCols) continue;
            for (let e = s.cellHead[ry * s.gridCols + rx]; e !== -1; e = s.cellNext[e]) {
              if (e <= i || !s.ep.alive[e]) continue;
              const defE = cfg.enemies[s.eDefIdx[e]]!;
              const dx = s.eX[e] - s.eX[i], dy = s.eY[e] - s.eY[i];
              // PACKING radius, not combat radius -- see PACKING_RADIUS_FRACTION's own comment.
              const rr = (defI.radius + defE.radius) * PACKING_RADIUS_FRACTION * SEPARATION_MARGIN;
              const d2 = dx * dx + dy * dy;
              if (d2 >= rr * rr) continue;
              let nx: number, ny: number, d: number;
              if (d2 < 1e-9) { nx = 1; ny = 0; d = 0; } else { d = Math.sqrt(d2); nx = dx / d; ny = dy / d; }
              const push = (rr - d) / 2;
              s.eX[i] -= nx * push; s.eY[i] -= ny * push;
              s.eX[e] += nx * push; s.eY[e] += ny * push;
              if (s.eX[i] < r.wallMargin) s.eX[i] = r.wallMargin;
              if (s.eX[i] > r.arenaWidth - r.wallMargin) s.eX[i] = r.arenaWidth - r.wallMargin;
              if (s.eX[e] < r.wallMargin) s.eX[e] = r.wallMargin;
              if (s.eX[e] > r.arenaWidth - r.wallMargin) s.eX[e] = r.arenaWidth - r.wallMargin;
            }
          }
        }
      }

      // Iron Rule 8 / boss-engagement invariant: a boss or beast stopped in
      // front of the squad this tick (section 8) BEFORE any of the above
      // could nudge it. Re-apply the same stop clamp every sweep so
      // separation can never shove a stopped body closer than its own
      // contact line, even transiently.
      for (let i = 0; i < s.ep.cap; i++) {
        if (!s.ep.alive[i]) continue;
        const def = cfg.enemies[s.eDefIdx[i]]!;
        if (def.isBoss || s.eIsBoss[i] === 1) {
          const stop = s.playerY + def.radius + r.playerHitRadius - 0.05;
          if (s.eY[i] < stop) s.eY[i] = stop;
        }
      }

      // LANE BOUND (founder finding, 2026-08-27, screenshot-verified against
      // the deployed build): pinning the horde and the conveyor to one lane
      // (Rules.hordeLane) roughly doubled that lane's body density without
      // changing how far a crowded body may be shoved -- SOLDIER-vs-ENEMY
      // and ENEMY-vs-ENEMY separation above only clamp `eX` to the ARENA's
      // own wallMargin, never to a lane boundary, so overcrowding pressure
      // shoved bodies clean across into the OTHER lane's screen space while
      // `eLane` (breach pricing, D13, every check this session's earlier
      // verification actually read) stayed exactly what it was authored
      // as. Invisible to eLane, plainly visible on screen -- which is
      // exactly why that earlier verification passed while the reported
      // bug did not go away. Re-applied every sweep, same pattern as the
      // boss stop-clamp just above. Skipped inside D13's own operating
      // window (dy <= 6, the same cutoff `inLaneRange` itself uses, see
      // section 8) so this can never fight the crossover lunge that is
      // SUPPOSED to move a body into the player's lane close to contact --
      // outside that window "a lane you can see is clear must STAY clear"
      // is the only rule, and this is what makes it a physical guarantee
      // instead of a spawn-time hope. Boss/beast are exempt: they already
      // cross lanes by design (lateralInRange), scaled to their own larger
      // radius.
      {
        const lanes = Math.max(1, r.laneCount);
        const laneW = r.arenaWidth / lanes;
        for (let i = 0; i < s.ep.cap; i++) {
          if (!s.ep.alive[i]) continue;
          const def = cfg.enemies[s.eDefIdx[i]]!;
          if (def.isBoss || s.eIsBoss[i] === 1) continue;
          if (s.eY[i] - s.playerY <= 6) continue;
          const lane = s.eLane[i];
          const lo = lane * laneW + def.radius;
          const hi = (lane + 1) * laneW - def.radius;
          if (s.eX[i] < lo) s.eX[i] = lo;
          if (s.eX[i] > hi) s.eX[i] = hi;
        }
      }
    }
  }

  // --- 16. end conditions ---------------------------------------------------
  // VARIANT B (founder 2026-08-21): THRESHOLD AT THE BOSS. damageSquad
  // floors squadSize at 1, so the wipe check below never fires and the
  // boss's own hp is never the gate -- the run resolves the instant the
  // boss spawns, on squadSize vs clashThreshold, not on out-damaging it.
  if (s.variant === 1) {
    if (s.bossSpawned && s.clashCrowd < 0) {
      s.clashCrowd = s.squadSize;
      const t = s.clashThreshold;
      const r = s.config.rules;
      s.outcome = s.squadSize >= t ? Outcome.BossKilled : Outcome.Died;
      // Per-chapter bands when levels.csv has a row for this chapter
      // (config.ts), else 1.15x/1.3x of clashThreshold -- see
      // Rules.star2Threshold/star3Threshold.
      s.stars = s.squadSize >= r.star3Threshold ? 3 : s.squadSize >= r.star2Threshold ? 2 : s.squadSize >= t ? 1 : 0;
      s.phase = Phase.Ended;
      return;
    }
    if (s.tick >= RUN_MAX_TICKS) { s.outcome = Outcome.Timeout; s.phase = Phase.Ended; }
    return;
  }
  if (s.squadSize <= 0) { s.outcome = Outcome.Died; s.phase = Phase.Ended; return; }
  // THE BOSS'S OWN DEATH ENDS THE RUN (founder-reported: it wasn't).
  // Measured across 30 seeds of real play: the waveCursor-gated check
  // below can't fire until a long BACKGROUND wave (chapter 1's wave 15,
  // 644 bodies dripped over 49.5s) finishes spawning, entirely unrelated
  // to the boss encounter -- the boss could die at 102s and the run kept
  // running until 119s regardless, because waveCursor was still stuck on
  // that wave's bookkeeping. This checks the boss's own death directly.
  //
  // Uses cfg.enemies[...].isBoss, the CONFIG flag, not s.eIsBoss -- that
  // state flag is shared with mid-run BEAST titans (spawnEnemy sets it
  // for `asBoss || def.isBoss || def.isBeast` alike, so a live gale_
  // colossus and a live stormhulk are indistinguishable through it).
  // Ending the run off s.eIsBoss would fire the moment any beast died,
  // awarding a win at ~41s the player never earned.
  if (s.bossSpawned && !anyTrueBossAlive(s)) {
    s.outcome = Outcome.BossKilled; s.phase = Phase.Ended; return;
  }
  // The CHAPTER is complete when every wave has spawned and no boss is
  // left standing. Ending on "the boss we most recently spawned died"
  // worked only while exactly one enemy in the whole game was flagged as
  // a boss; with a boss per act it ended the run at the first one, at
  // ~27s instead of ~206s. A chapter may hold any number of bosses.
  // Left in place as a fallback: the check above should always preempt
  // it in every authored chapter (the boss always spawns and dies before
  // waveCursor could otherwise catch up), so this is now unreachable in
  // practice rather than removed.
  if (s.bossSpawned && s.waveCursor >= cfg.waves.length && !anyBossAlive(s)) {
    s.outcome = Outcome.BossKilled; s.phase = Phase.Ended; return;
  }
  if (s.tick >= RUN_MAX_TICKS) { s.outcome = Outcome.Timeout; s.phase = Phase.Ended; }
}

/** True while any boss-flagged spawn is still on the field. */
function anyBossAlive(s: RunState): boolean {
  for (let i = 0; i < s.ep.cap; i++) {
    if (s.ep.alive[i] && s.eIsBoss[i] === 1) return true;
  }
  return false;
}

/**
 * True while a REAL chapter boss is still on the field, by the config's
 * own isBoss flag -- not s.eIsBoss, which also covers mid-run beasts
 * (see the end-conditions comment above).
 */
function anyTrueBossAlive(s: RunState): boolean {
  const cfg = s.config;
  for (let i = 0; i < s.ep.cap; i++) {
    if (s.ep.alive[i] && cfg.enemies[s.eDefIdx[i]]!.isBoss) return true;
  }
  return false;
}

function killEnemy(
  s: RunState, e: number,
  cause: number = DeathCause.Attrition, dx = 0, dy = 0,
): void {
  const def = s.config.enemies[s.eDefIdx[e]]!;
  s.killCount++;
  // A body that was erased and a body that ran out of health must not fall
  // the same way, so the cause and the blow's direction ride on the event
  // rather than being inferred by the renderer from the corpse's position.
  // defIdx stays on the record because the subject's slot may be recycled
  // before the renderer reads the tick.
  const [ddx, ddy] = unitDir(dx, dy);
  emit(s, Ev.Death, e, cause, ddx, ddy, s.eDefIdx[e]);

  const ki = poolAlloc(s.kp);
  if (ki >= 0) {
    // Clamp the drop into the arena. Projectiles out-range the arena
    // ceiling, so an unclamped orb lands where the player is not allowed
    // to go and its XP is stranded for the rest of the run.
    const r = s.config.rules;
    s.kX[ki] = s.eX[e];
    s.kY[ki] = s.eY[e] > r.arenaHeight - 0.5 ? r.arenaHeight - 0.5 : s.eY[e];
    s.kKind[ki] = PICKUP_XP; s.kValue[ki] = def.xpValue;
  }
  // Recruits. Rolled BEFORE the weapon draw and always, so the stream
  // sequence never depends on pool occupancy.
  //
  // KILLS SUSTAIN THE SQUAD; ONLY REWARDS GROW IT. That rule has been in
  // rules.csv since the drop chance was set, but a FLAT chance cannot
  // implement it: recruit income scales linearly with kill volume, so
  // every density increase silently re-arms kill-growth. Measured at the
  // n=32 baseline: greedy winners on isle 1 picked up 92-114 recruit
  // bodies against a net gate delta of -55 -- the horde out-recruited the
  // reward lane, and a player who refused every reward won 19% of runs.
  // The gate below is the rule made literal: kills REFILL toward the
  // squad's own high-water mark; only the reward lane raises the mark.
  // The gate has a FLOOR because the OPENING is legitimately kill-fed: at
  // squad_start=1 the peak is 1, a peak-only gate drops nothing before the
  // first barrier at 14s, and a one-man squad against a prefilled street
  // died at 16s on every seed measured (0/32). Kills assemble the fireteam
  // up to the floor; from there only the reward lane makes it an army.
  const rr = nextFloat(s.rng, Stream.Drops);
  const boss = s.eIsBoss[e] === 1;
  const growCeil = s.squadPeak > s.config.rules.recruitGrowthFloor
    ? s.squadPeak : s.config.rules.recruitGrowthFloor;
  const recruits = boss ? Math.round(s.config.rules.recruitDropBoss)
    : rr < s.config.rules.recruitDropChance && s.squadSize < growCeil ? 1 : 0;
  for (let n = 0; n < recruits; n++) {
    const ri = poolAlloc(s.kp);
    if (ri < 0) break;
    const rc = s.config.rules;
    s.kX[ri] = s.eX[e];
    s.kY[ri] = s.eY[e] > rc.arenaHeight - 0.5 ? rc.arenaHeight - 0.5 : s.eY[e];
    s.kKind[ri] = PICKUP_RECRUIT; s.kValue[ri] = 1;
  }
  // Weapon drops roll on the `drops` stream, always, so the draw sequence
  // does not depend on whether a pickup pool slot happened to be free.
  // Weapon drops CATCH UP. At a flat 2% and four ARMS gates a chapter, a
  // player who takes multiplier lanes can hold one gun for minutes --
  // measured: Scatterwind held from 54s to 166s, 112 consecutive seconds,
  // so the ten-weapon library was barely seen. The further behind the
  // progress ceiling you are, the likelier a drop becomes.
  const behind = Math.max(0, weaponCeiling(s) - s.weaponIdx);
  const dropChance = s.config.rules.weaponDropChance * (1 + behind * 2);
  const roll = nextFloat(s.rng, Stream.Drops);
  if (roll < dropChance && s.config.weapons.length > 1) {
    const wi = drawWeapon(s, Stream.Drops);
    const pi = poolAlloc(s.kp);
    if (pi >= 0) {
      s.kX[pi] = s.eX[e]; s.kY[pi] = s.eY[e];
      s.kKind[pi] = PICKUP_WEAPON; s.kValue[pi] = wi;
    }
  }
  poolFree(s.ep, e);
  if (e === s.bossIdx) s.bossPhase = 2;
}

/**
 * Deploy a monster won from a charge station.
 *
 * Scaled off the squad's own strength rather than authored flat, so an
 * ally is always worth roughly the same share of a run: a fixed 2000hp
 * beast is a god at 60s and a speed bump at 200s, and the station would
 * stop being worth charging exactly when it costs the most to charge.
 */
/**
 * Release the titan a monolith was holding.
 *
 * A titan is an ally with the numbers of a set-piece: it is what the
 * player bought by NOT shooting the horde for the better part of a minute,
 * so it has to change the shape of the fight, not join it politely. Each
 * kind trades the same budget differently.
 */
/**
 * A broken forge block PAYS (founder, 2026-08-14). Every payload answers
 * "where does the prize act?": bodies join the formation, arms/armour/
 * vehicle attach to the squad, and anything that FIGHTS — the titan —
 * deploys INTO THE HORDE LANE, because a monster patrolling the empty
 * reward lane guards nothing. Events reuse the gate vocabulary so the
 * renderer and the cause-audit read blocks and gates identically.
 */
function breakBlock(s: RunState, mi: number): void {
  const op = s.mOp[mi];
  if (op === BLOCK_TITAN) { releaseTitan(s, mi); return; }
  if (op === BLOCK_BODIES) {
    const before = s.squadSize;
    gainSquad(s, before + Math.round(s.mValue[mi]));
    emit(s, Ev.BarrierDown, -1, Math.round(s.mValue[mi]), s.squadSize - before);
    return;
  }
  if (op === BLOCK_RACE) {
    // ISLE-12 REBUILD: soldiers = floor(remaining/total * R_i), where
    // `remaining`/`total` are approach TIME (equivalently distance, since
    // drift rate is constant), not HP -- HP only gates WHETHER this path
    // runs at all (breakBlock is called exactly once, the tick hp crosses
    // <= 0). `total` is the fixed gap this chapter always spawns a race
    // monolith at (spawnMonolith: spawnAhead*monolithSpawnFrac),
    // recomputed from rules rather than stored per-instance since it's
    // the same for every one.
    // `remaining` reads the CURRENT mY/playerY gap, so it stays correct
    // even if the player moved during the approach. Not clamped (founder:
    // "Do not clamp hp; overkill does not add") -- this formula never
    // reads hp at all, so overkill magnitude cannot inflate it regardless.
    const rr = s.config.rules;
    const driftPerTick = (rr.scrollSpeed * 0.22) / TICK_RATE;
    // VISIBLE-KILL AUDIT PART 2: reads the SAME `monolithSpawnFrac`
    // spawnMonolith itself uses for the spawn position, so moving that
    // fraction can never desync the reward math from the actual distance.
    const totalTicks = (rr.spawnAhead * rr.monolithSpawnFrac) / driftPerTick;
    const remainingTicks = (s.mY[mi] - s.playerY) / driftPerTick;
    const before = s.squadSize;
    // SNOWBALL DAMPENER (AUDIT.md A-41, founder-requested fix 2026-08-29):
    // the raw formula pays MORE the faster a monolith dies, and a bigger
    // squad kills faster -- a closed, unbounded loop (bigger squad -> faster
    // kill -> more remainingTicks left at the kill -> payout closer to full
    // mValue -> bigger squad). Taxed by CURRENT squad size (`before`, read
    // pre-kill) rather than by cutting mValue itself: two attempts to
    // reduce mValue directly (a config change, not this one) both collapsed
    // winrate to 0% instead of gently slowing growth, because a flat cut
    // punishes every squad size equally including the small ones that need
    // the reward most. This instead punishes the SIZE that's driving the
    // loop, leaving early-game payouts close to full and only compressing
    // the late-game runaway. raceRewardDampenK=0 reproduces the old formula
    // exactly (rollback path); the tuned value is swept and verified
    // before being set in rules.csv, not guessed.
    const dampen = 1 / (1 + before * rr.raceRewardDampenK);
    const reward = Math.floor((remainingTicks / totalTicks) * s.mValue[mi] * dampen);
    gainSquad(s, before + Math.max(0, reward));
    emit(s, Ev.BarrierDown, -1, reward, s.squadSize - before);
    return;
  }
  if (op === BLOCK_CRATE) {
    const next = drawWeapon(s, Stream.UpgradeOffers);
    if (next > s.weaponIdx) { s.weaponIdx = next; recomputeStats(s); }
    emit(s, Ev.Armory, -1, s.weaponIdx, 1);
    return;
  }
  if (op === BLOCK_ARMOUR) {
    const add = unitHp(s) * s.mValue[mi];
    s.shieldHp += add;
    if (s.shieldHp > s.shieldMax) s.shieldMax = s.shieldHp;
    emit(s, Ev.Charged, -1, 2, 8);
    return;
  }
  // BLOCK_VEHICLE: the tank rides with the squad.
  s.vehicleTicks = Math.round(s.config.rules.vehicleDurationSec * TICK_RATE);
  recomputeStats(s);
  emit(s, Ev.Armory, -1, s.weaponIdx, 2);
  emit(s, Ev.Charged, -1, 2, 6);
}

function releaseTitan(s: RunState, mi: number): void {
  const idx = poolAlloc(s.lp);
  if (idx < 0) return;
  const r = s.config.rules;
  const kind = s.mTitan[mi];
  // Sized off the squad for the same reason an ally is: a flat number is a
  // god early and furniture late.
  const base = 900 + s.squadSize * 380;
  const dmg = 150 + s.squadSize * 38;
  const half = r.wallMargin + 0.5;
  // THE MONSTER FIGHTS WHERE THE WAR IS (founder, 2026-08-14): on a forge
  // isle the titan you freed in the reward lane walks out INTO the horde
  // lane. A monster patrolling an enemy-free lane kills nothing.
  const lanes = Math.max(1, r.laneCount);
  let ax = r.rewardLane >= 0
    ? laneCenter(r.arenaWidth, lanes, r.rewardLane === 0 ? 1 : 0)
    : s.mX[mi];
  if (ax < half) ax = half;
  else if (ax > r.arenaWidth - half) ax = r.arenaWidth - half;
  s.lX[idx] = ax;
  s.lY[idx] = s.playerY + 8.0;
  const hp = base * (kind === Titan.Bulwark ? 2.6 : kind === Titan.Mech ? 1.1 : 1.0);
  s.lHp[idx] = hp; s.lHpMax[idx] = hp;
  s.lCooldown[idx] = 0;
  s.lPower[idx] = dmg * (kind === Titan.Mech ? 2.4 : kind === Titan.Bulwark ? 0.45
    : kind === Titan.Artillery ? 0.7 : 1.3);
  s.lRadius[idx] = kind === Titan.Artillery ? 3.6 : kind === Titan.Mech ? 2.4 : 2.0;
  s.lKind[idx] = kind;
  emit(s, Ev.TitanFreed, mi, kind);
  emit(s, Ev.Ally, idx, 0);
}

function spawnAlly(s: RunState, lane: number, power: number): void {
  const idx = poolAlloc(s.lp);
  if (idx < 0) return;
  const r = s.config.rules;
  const lanes = Math.max(1, r.laneCount);
  // PHYSICS LAW: an ally is the widest body in the game, so its centre is
  // clamped by its own half-width, not the shared margin. A lane centre is
  // safe today, but a future lane count or a knockback must not be able to
  // push those shoulders through a rail.
  const half = r.wallMargin + 0.35;
  let ax = laneCenter(r.arenaWidth, lanes, lane);
  if (ax < half) ax = half;
  else if (ax > r.arenaWidth - half) ax = r.arenaWidth - half;
  s.lX[idx] = ax;
  // Walks out AHEAD of the squad, not on top of it: spawned at +3 it stood
  // in the camera's lap and hid the corridor it was clearing.
  s.lY[idx] = s.playerY + 7.0;
  const hp = (400 + s.squadSize * 170) * power;
  s.lHp[idx] = hp; s.lHpMax[idx] = hp;
  s.lCooldown[idx] = 0;
  s.lPower[idx] = (90 + s.squadSize * 22) * power;
  s.lRadius[idx] = 1.5 + 0.7 * power;
  // Station allies are saurians -- the baseline body.
  s.lKind[idx] = 0;
  emit(s, Ev.Ally, idx, 0);
}

/**
 * @param dodgeable A-43: true for a genuine combat hit (contact, breach,
 * boss/beast swing, projectile) — false for a deliberate economic cost
 * (ramming an intact barrier, fizzling a charge station) that the player
 * chose by not charging in time, not something a reflex could avoid.
 * Dodging a toll you elected to pay would make the toll meaningless.
 */
function damageSquad(s: RunState, amount: number, dodgeable = true): void {
  if (dodgeable && s.dodgeActiveTicks > 0) return;
  // ARMOUR FIRST. The shield is the only thing in the run that can take a
  // hit without costing bodies, which is what makes charging one feel
  // different from every other reward.
  if (s.shieldHp > 0 && amount > 0) {
    const soak = amount < s.shieldHp ? amount : s.shieldHp;
    s.shieldHp -= soak;
    amount -= soak;
    emit(s, Ev.ShieldHit, -1, Math.round(soak), Math.round(s.shieldHp));
    if (amount <= 0) return;
  }
  s.squadHpPool -= amount;
  // VARIANT B FLOOR (founder 2026-08-21): THRESHOLD AT THE BOSS never
  // wipes mid-run -- the run always reaches the clash, and skill shows in
  // how far above s.clashThreshold the squad lands there, not in whether
  // it survives to see it.
  const floor = s.variant === 1 ? unitHp(s) : 0;
  if (s.squadHpPool < floor) s.squadHpPool = floor;
  const size = Math.ceil(s.squadHpPool / unitHp(s));
  const minSize = s.variant === 1 ? 1 : 0;
  s.squadSize = size < minSize ? minSize : size;
  emit(s, Ev.SquadHit, -1, amount, s.squadSize);
}
